import axios from 'axios';
import { ExportToCsv } from 'export-to-csv';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { DateTime } from 'luxon';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { MdCheck, MdWhatsapp } from 'react-icons/md';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import InputIcon from 'react-multi-date-picker/components/input_icon';
import { useAuth0 } from '@auth0/auth0-react';
import { Alert, Button, Card, CardBody, Checkbox, Typography } from '@material-tailwind/react';

import downloadBaselineReviewPowerpoint from './BaselineReviewDownload';
import LoadingSpinner from './LoadingSpinner';
import LoadingSpinnerWithCross from './LoadingSpinnerWithCross';
import downloadPropertyMonthlyReviewPowerpoint from './PropertyMonthlyReviewDownload';
import SearchBar from '../Common/SearchBar';
import AppAlert from '../CompanyComponents/alerts/Alert';
import CompanyContext from '../../context/CompanyContext';
import LumiticsLogo from '../../images/Lumitics-logo-green-0.png';
import {
  CompanyLocationOption,
  CompanyService,
  DateTopWasteDetailByService,
  DateTopWasteImageSignedUrlByWeight,
  LocationMonthlyReportData,
  LocationService,
  RestaurantService,
  Service,
  ServiceBaselineDate,
} from '../../interfaces';
import EditMonthlyReviewSlideDialog from './EditMonthlyReviewSlideDialog';
import {
  getDefaultPropertyMonthlyReviewSlideMetadata,
  sortByNamePriority,
} from './EditMonthlyReviewSlide/helper';
import downloadPropertyMenuItemMappingSpreadsheet from './PropertyMenuItemMappingDownload';
import uploadPropertyMenuItemMappingSpreadsheet from './PropertyMenuItemMappingUpload';

const CALCULATOR_CONNECTION_ERROR = 'CalculatorConnectionError';
const WHATSAPP_CONNECTION_ERROR = 'WhatsappConnectionError';

/**
 * This function retrieves the date of the previous month's first and last dates to be used as default values of datepicker
 * @returns Object - Object containing first date and last date of previous month
 * @returns Object.firstDateOfPrevMonth - first date of previous month
 * @returns Object.lastDateOfPrevMonth - last date of previous month
 */
const getFirstAndLastDateOfPrevMonth = () => {
  const currentDate = new Date();
  const firstDateOfPrevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
  const lastDateOfPrevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
  return {
    firstDateOfPrevMonth: new DateObject(firstDateOfPrevMonth),
    lastDateOfPrevMonth: new DateObject(lastDateOfPrevMonth),
  };
};

/**
 * This function retrieves all valid company services
 * @property arrCompanyService - Array of all companyService
 * @returns arrValidCompanyService - Array of all valid companyService, where only valid services are included. Note that array of valid service can be empty.
 */
const getArrValidCompanyService = (arrCompanyService: Array<CompanyService>) => {
  const arrValidCompanyService: Array<CompanyService> = [];
  arrCompanyService.forEach((companyService) => {
    const { arrRestaurantService, ...company } = companyService;
    const validCompanyService: CompanyService = {
      ...company,
      arrRestaurantService: [],
    };
    arrRestaurantService.forEach((restaurantService) => {
      const { arrLocationService, ...restaurant } = restaurantService;
      const validRestaurantService: RestaurantService = {
        ...restaurant,
        arrLocationService: [],
      };
      arrLocationService.forEach((locationService) => {
        const { arrService, ...location } = locationService;
        const validLocationService = {
          ...location,
          arrService: arrService.filter(
            (service) =>
              service.projectEndDate === null &&
              service.arrServiceBaselineDate.length > 0 &&
              service.serviceType.type !== 'Placeholder'
          ),
        };
        if (validLocationService.arrService.length !== 0)
          validRestaurantService.arrLocationService.push(validLocationService);
      });
      if (validRestaurantService.arrLocationService.length !== 0)
        validCompanyService.arrRestaurantService.push(validRestaurantService);
    });
    if (validCompanyService.arrRestaurantService.length !== 0)
      arrValidCompanyService.push(validCompanyService);
  });
  return arrValidCompanyService;
};

const ERROR_MESSAGE = {
  noDataInDateRange: 'Specified date range has no report data',
  fetchReportError: 'Error fetching report data from database',
  noImageInDateRange: 'Specified date range has no images',
  fetchImageError: 'Error fetching images from database',
  fetchRestaurantError: 'Error fetching property data',
  fetchPropertyMonthlyReviewError: 'Error fetching property review report',
  calculatorConnectionError: 'Connection error. Please try again a few minutes later.',
  fetchBaselineReviewError: 'Error fetching baseline review report',
  issueHasNegativeWeightWasteError:
    'Report has been successfully downloaded but there is at least 1 negative weight waste in the issue slides',
  fetchPropertyTopWasteError: 'Error fetching property top waste images',
  dateChosenExceedSevenDaysError:
    'Property top wastes has been successfully downloaded but only for the first 7 days',
  fetchPropertyMenuItemMappingDownloadError: 'Error fetching property menu item mapping',
  fetchPropertyMenuItemMappingUploadError: 'Error uploading property menu item mapping',
  whatsappConnectionError: 'Whatsapp connection error. Please inform Software Team.',
};

const ERROR_TYPE = {
  fatal: 'fatal',
  warning: 'warning',
};

const IS_TAGGED_VALUES: { [key: string]: boolean | string | undefined } = {
  all: undefined,
  allExceptUntagged: 'allExceptUntagged',
  taggedAndResolvable: 'taggedAndResolvable',
  taggedAndMapped: true,
  untagged: false,
};

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const QUICK_SELECT_OPTION = {
  yesterday: 'Yesterday',
  lastWeek: 'Last Week',
  lastMonth: 'Last Month',
  last3Months: 'Last 3 Months',
  fromPropertyEarliestStartDate: 'From Property Earliest Start Date',
};

/**
 * Function gets base64 data url of a slide image
 * @param url - Path of slide image
 * @returns String - base64 data url of a slide image
 */
const getImageBase64Url = async (url: string): Promise<string> => {
  const response = await axios.get(url, {
    responseType: 'blob',
  });
  const blob = response.data;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });
};

/**
 * Function gets base64 data url of slide images for property monthly review
 * @returns Object containing base64 data url of slide images for property monthly review
 */
const getAllImageBase64Urls = async (): Promise<{ [key: string]: string }> => {
  return {
    openingBackground: await getImageBase64Url('/openingBackground.png'),
    plainBackground: await getImageBase64Url('/plainBackground.png'),
    sectionBackground: await getImageBase64Url('/sectionBackground.png'),
    closingBackground: await getImageBase64Url('/closingBackground.png'),
    lightbulb: await getImageBase64Url('/lightbulb.png'),
  };
};

const ReportInputCard = () => {
  const companyContext = useContext(CompanyContext);
  const propertyTopWasteRef = useRef<any>();
  const { firstDateOfPrevMonth, lastDateOfPrevMonth } = getFirstAndLastDateOfPrevMonth();
  const { user } = useAuth0();

  const initialFormState: {
    companyField: string;
    restaurantField: string;
    mapSetSelectedServiceIdBySelectedLocationId: Map<number, Set<number>>;
    dateRange: Array<DateObject>;
    isTagged: boolean | string | undefined;
    includeIssueSlides: boolean;
    includeMenuItemName: boolean;
  } = {
    companyField: 'Select Group',
    restaurantField: 'Select Property',
    mapSetSelectedServiceIdBySelectedLocationId: new Map(),
    dateRange: [firstDateOfPrevMonth, lastDateOfPrevMonth],
    isTagged: IS_TAGGED_VALUES.taggedAndResolvable,
    includeIssueSlides: true,
    includeMenuItemName: false,
  };

  const initialError: {
    isVisible: boolean;
    message: string;
    errorType: string;
  } = {
    isVisible: false,
    message: '',
    errorType: '',
  };

  const [alertHeader, setAlertHeader] = useState('');
  const [arrAlertMessage, setArrAlertMessage] = useState<Array<any>>([]);
  const [arrValidCompanyService, setArrValidCompanyService] = useState<Array<CompanyService>>([]);
  const [error, setError] = useState(initialError);
  const [formState, setFormState] = useState(initialFormState);
  const [isAlert, setIsAlert] = useState(false);
  const [isCompanyReportLoading, setIsCompanyReportLoading] = useState<boolean>(false);
  const [isRestaurantReportLoading, setIsRestaurantReportLoading] = useState<boolean>(false);
  const [isLocationReportLoading, setIsLocationReportLoading] = useState<boolean>(false);
  const [isCompanyDailyWasteReportLoading, setIsCompanyDailyWasteReportLoading] =
    useState<boolean>(false);
  const [isRestaurantDailyWasteReportLoading, setIsRestaurantDailyWasteReportLoading] =
    useState<boolean>(false);
  const [isLocationDailyWasteReportLoading, setIsLocationDailyWasteReportLoading] =
    useState<boolean>(false);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);
  const [minDate, setMinDate] = useState<undefined | DateObject>(undefined);
  const [isPropertyMonthlyReviewLoading, setIsPropertyMonthlyReviewLoading] =
    useState<boolean>(false);
  const [isSingleDayPropertyTopWasteLoading, setIsSingleDayPropertyTopWasteLoading] =
    useState<boolean>(false);
  const [isMultiDayPropertyTopWasteLoading, setIsMultiDayPropertyTopWasteLoading] =
    useState<boolean>(false);
  const [isBaselineReviewLoading, setIsBaselineReviewLoading] = useState<boolean>(false);
  const [minStartDate, setMinStartDate] = useState<undefined | DateObject>(undefined);
  const [dataToDisplay, setDataToDisplay] = useState<any>(null);
  const [setLocationIdExpanded, setSetLocationIdExpanded] = useState<Set<number>>(new Set());
  const [isEditMonthlyReviewSlideDialogOpened, setIsEditMonthlyReviewSlideDialogOpened] =
    useState<boolean>(false);
  const [
    currentRestaurantIdEditingMonthlyReviewSlideMetadata,
    setCurrentRestaurantIdEditingMonthlyReviewSlideMetadata,
  ] = useState<number | null>(null);
  const [isPropertyMenuItemMappingDownloadLoading, setIsPropertyMenuItemMappingDownloadLoading] =
    useState<boolean>(false);
  const [isPropertyMenuItemMappingUploadLoading, setIsPropertyMenuItemMappingUploadLoading] =
    useState<boolean>(false);
  const [isWhatsappLoading, setIsWhatsappLoading] = useState<boolean>(false);
  const [arrWhatsappMessagingSessionOption, setArrWhatsappMessagingSessionOption] =
    useState<Array<string> | null>(null);
  const [whatsappMessagingSessionSelected, setWhatsappMessagingSessionSelected] = useState<
    string | undefined
  >('Select WA Session');

  const groupReportController = useRef<null | AbortController>(null);
  const isGroupReportCancelledRef = useRef(false);
  const propertyReportController = useRef<null | AbortController>(null);
  const isPropertyReportCancelledRef = useRef(false);
  const locationReportController = useRef<null | AbortController>(null);
  const isLocationReportCancelledRef = useRef(false);
  const groupDailyWasteReportController = useRef<null | AbortController>(null);
  const isGroupDailyWasteReportCancelledRef = useRef(false);
  const propertyDailyWasteReportController = useRef<null | AbortController>(null);
  const isPropertyDailyWasteReportCancelledRef = useRef(false);
  const locationDailyWasteReportController = useRef<null | AbortController>(null);
  const isLocationDailyWasteReportCancelledRef = useRef(false);
  const singleDayPropertyTopWasteController = useRef<null | AbortController>(null);
  const isSingleDayPropertyTopWasteCancelledRef = useRef(false);
  const multiDayPropertyTopWasteController = useRef<null | AbortController>(null);
  const isMultiDayPropertyTopWasteCancelledRef = useRef(false);
  const propertyMenuItemMappingDownloadController = useRef<null | AbortController>(null);
  const isPropertyMenuItemMappingDownloadCancelledRef = useRef(false);
  const baselineReviewController = useRef<null | AbortController>(null);
  const isBaselineReviewCancelledRef = useRef(false);
  const propertyMonthlyReviewController = useRef<null | AbortController>(null);
  const isPropertyMonthlyReviewCancelledRef = useRef(false);
  const serviceImagesController = useRef<null | AbortController>(null);
  const isServiceImagesCancelledRef = useRef(false);

  useEffect(() => {
    setArrValidCompanyService(getArrValidCompanyService(companyContext.arrCompanyService));
  }, [companyContext.arrCompanyService]);

  useEffect(() => {
    if (
      formState.restaurantField !== 'Select Property' &&
      JSON.parse(formState.restaurantField).whatsappGroupName !== 'null' &&
      arrWhatsappMessagingSessionOption === null
    ) {
      fetchArrWhatsappMessagingSessionOption();
    }
  }, [formState.restaurantField]);

  /**
   * This function fetches whatsapp sessions to allow users to choose where to send messages from
   */
  const fetchArrWhatsappMessagingSessionOption = async () => {
    try {
      setIsWhatsappLoading(true);
      const response = await axios.post('/report-management/fetch-whatsapp-messaging-session', {
        email: user!.email,
      });
      const { arrWhatsappMessagingSession, defaultSession } = response.data;
      const arrWhatsappMessagingSessionName = arrWhatsappMessagingSession.map(
        (whatsappMessagingSession: any) => whatsappMessagingSession.name
      );
      setArrWhatsappMessagingSessionOption(arrWhatsappMessagingSessionName);
      if (defaultSession !== null && arrWhatsappMessagingSessionName.includes(defaultSession)) {
        setWhatsappMessagingSessionSelected(defaultSession);
      }
    } catch (error: any) {
      displayError(ERROR_MESSAGE.whatsappConnectionError, ERROR_TYPE.fatal);
    } finally {
      setIsWhatsappLoading(false);
    }
  };

  /**
   * This function takes in display message to be displayed and sets the error state to the message
   * @param message String - Message to be displayed
   */
  const displayError = (message: string, errorType: string) => {
    setError({
      isVisible: true,
      message,
      errorType,
    });
  };

  /**
   * This function resets the error message back to its initial value
   */
  const resetErrorMessage = () => setError(initialError);

  /**
   * This function sets the isCompanyReportLoading / isRestaurantReportLoading / isLocationReportLoading states to true or false
   * depending on whichever report the user is downloading
   * @param findBy - 'company', 'restaurant', 'location'
   * @param isTrue - boolean to set the states to true or false
   */
  const setIsReportLoading = (findBy: string, isTrue: boolean) => {
    if (findBy === 'company') {
      setIsCompanyReportLoading(isTrue);
    } else if (findBy === 'restaurant') {
      setIsRestaurantReportLoading(isTrue);
    } else if (findBy === 'location') {
      setIsLocationReportLoading(isTrue);
    }
  };

  /**
   * This function handles the generation of csv file for download.
   * During the process, there is a check to stop the function when isPropertyReportCancelledRef.current or isLocationReportCancelledRef.current is false.
   */
  const generateReport = async (findBy: string) => {
    try {
      setIsReportLoading(findBy, true);
      const {
        companyField,
        restaurantField,
        mapSetSelectedServiceIdBySelectedLocationId,
        dateRange,
      } = formState;
      const [fromDate, toDate] = dateRange;
      let id;
      let fileName;
      const companyFieldParsed = JSON.parse(companyField);
      const { isAirline, name: companyName, companyId, index: companyIndex } = companyFieldParsed;
      if (findBy === 'company') {
        id = companyId;
        fileName = `Group_${companyName}_${fromDate}_${toDate}`;
      } else if (findBy === 'restaurant') {
        isPropertyReportCancelledRef.current = false;
        propertyReportController.current = new AbortController();
        const { restaurantId, name: restaurantName } = JSON.parse(restaurantField);
        id = restaurantId;
        fileName = `Property_${companyName}_${restaurantName}_${fromDate}_${toDate}`;
      } else if (findBy === 'location') {
        isLocationReportCancelledRef.current = false;
        locationReportController.current = new AbortController();
        const companyService = arrValidCompanyService[companyIndex];
        const { name: restaurantName, index: restaurantIndex } = JSON.parse(restaurantField);
        // Only 1 location is selected to enable the location generateReport button
        const [[locationId]] = Array.from(mapSetSelectedServiceIdBySelectedLocationId.entries());
        const restaurantService = companyService.arrRestaurantService[restaurantIndex];
        const locationService = restaurantService.arrLocationService.find(
          (locationService) => locationService.locationId === locationId
        )!;
        id = locationId;
        fileName = `Location_${companyName}_${restaurantName}_${locationService.name}_${fromDate}_${toDate}`;
      }
      if (
        (findBy === 'restaurant' && isPropertyReportCancelledRef.current) ||
        (findBy === 'location' && isLocationReportCancelledRef.current)
      )
        return;
      const startDate = fromDate.format('YYYY-MM-DD');
      const endDate = toDate.format('YYYY-MM-DD');
      const response = await axios.post(
        '/report-management/fetch-report',
        {
          isAirline,
          findBy,
          attribute: {
            id,
            startDate,
            endDate,
          },
        },
        {
          signal:
            findBy === 'restaurant'
              ? propertyReportController?.current?.signal
              : locationReportController?.current?.signal, // This function is currently only used by property report and location report only
        }
      );

      const { arrReport } = response.data;
      if (
        (findBy === 'restaurant' && isPropertyReportCancelledRef.current) ||
        (findBy === 'location' && isLocationReportCancelledRef.current)
      )
        return;
      // If returned arrReport has length 0, display 'no data' error
      if (arrReport.length === 0) {
        displayError(ERROR_MESSAGE.noDataInDateRange, ERROR_TYPE.fatal);
        setIsReportLoading(findBy, false);
        return;
      }
      if (
        (findBy === 'restaurant' && isPropertyReportCancelledRef.current) ||
        (findBy === 'location' && isLocationReportCancelledRef.current)
      )
        return;
      const options = {
        filename: fileName,
        quoteStrings: '',
        showLabels: true,
        useKeysAsHeaders: true,
      };
      const csvExporter = new ExportToCsv(options);
      csvExporter.generateCsv(arrReport);
    } catch (error: any) {
      if (!(axios.isCancel(error) || error.name === 'CanceledError')) {
        displayError(ERROR_MESSAGE.fetchReportError, ERROR_TYPE.fatal);
      }
    } finally {
      setIsReportLoading(findBy, false);
    }
  };

  /**
   * Generate group report (a.k.a corporate report)
   * Format is referenced and standardised in the backend. This function receives the data from the backend
   * and generates the csv for download
   * Additionally, service report is also downloaded such that other customised reports can be created from it
   * During the process, there is a check to stop the function when isGroupReportCancelledRef.current is false.
   */
  const generateGroupReport = async () => {
    try {
      setIsReportLoading('company', true);
      isGroupReportCancelledRef.current = false;
      groupReportController.current = new AbortController();
      const { dateRange, companyField } = formState;
      const [fromDate, toDate] = dateRange;
      const startDate = fromDate.format('YYYY-MM-DD');
      const endDate = toDate.format('YYYY-MM-DD');
      const companyFieldParsed = JSON.parse(companyField);
      const companyService = arrValidCompanyService[Number(companyFieldParsed.index)];
      const arrRestaurantServiceToFetchData: RestaurantService[] = [];
      companyService.arrRestaurantService.forEach((restaurantService) => {
        const arrLocationServiceToFetchData: LocationService[] = [];
        restaurantService.arrLocationService.forEach((locationService) => {
          const { arrService } = locationService;
          const arrValidService = arrService.filter(
            (service) =>
              service.projectEndDate === null && service.arrServiceBaselineDate.length > 0
          );
          arrLocationServiceToFetchData.push({
            ...locationService,
            arrService: arrValidService,
          });
        });
        if (arrLocationServiceToFetchData.length !== 0) {
          arrRestaurantServiceToFetchData.push({
            ...restaurantService,
            arrLocationService: arrLocationServiceToFetchData,
          });
        }
      });
      const companyServiceToFetchData = {
        ...companyService,
        arrRestaurantService: arrRestaurantServiceToFetchData,
      };

      const response = await axios.post(
        '/report-management/fetch-group-report-data',
        {
          companyService: companyServiceToFetchData,
          dateRange: {
            startDate,
            endDate,
          },
        },
        {
          signal: groupReportController.current.signal,
        }
      );
      if (isGroupReportCancelledRef.current) return;
      const { arrGroupLevelReportData, arrPropertyLevelReportData, arrServiceLevelReportData } =
        response.data.groupReportData;

      const groupLevelCsvOption = {
        fieldSeparator: ',',
        quoteStrings: '',
        decimalSeparator: '.',
        filename: `Group_${companyService.name}_${fromDate}_${toDate}`,
      };
      const groupLevelCsvExporter = new ExportToCsv(groupLevelCsvOption);
      const groupLevelCsv = groupLevelCsvExporter.generateCsv(arrGroupLevelReportData, true);

      const propertyLevelCsvOption = {
        fieldSeparator: ',',
        quoteStrings: '',
        decimalSeparator: '.',
        showTitle: true,
        title: groupLevelCsv,
        filename: `Group_${companyService.name}_${fromDate}_${toDate}`,
        useKeysAsHeaders: true,
      };
      const propertyLevelCsvExporter = new ExportToCsv(propertyLevelCsvOption);
      propertyLevelCsvExporter.generateCsv(arrPropertyLevelReportData);

      const serviceLevelCsvOption = {
        fieldSeparator: ',',
        quoteStrings: '',
        decimalSeparator: '.',
        filename: `Service_Breakdown_Group_${companyService.name}_${fromDate}_${toDate}`,
        useKeysAsHeaders: true,
      };
      const serviceLevelCsvExporter = new ExportToCsv(serviceLevelCsvOption);
      serviceLevelCsvExporter.generateCsv(arrServiceLevelReportData);
    } catch (error: any) {
      if (!(axios.isCancel(error) || error.name === 'CanceledError')) {
        if (error?.response?.data.error === CALCULATOR_CONNECTION_ERROR) {
          displayError(ERROR_MESSAGE.calculatorConnectionError, ERROR_TYPE.fatal);
        } else {
          displayError(ERROR_MESSAGE.fetchReportError, ERROR_TYPE.fatal);
        }
      }
    } finally {
      setIsReportLoading('company', false);
    }
  };

  /**
   * This function sets the isCompanyDailyWasteReportLoading / isRestaurantDailyWasteReportLoading / isLocationDailyWasteReportLoading states to true or false
   * depending on whichever report the user is downloading
   * @param findBy - 'company', 'restaurant', 'location'
   * @param isTrue - boolean to set the states to true or false
   */
  const setIsDailyWasteReportLoading = (findBy: string, isTrue: boolean) => {
    if (findBy === 'company') {
      setIsCompanyDailyWasteReportLoading(isTrue);
    } else if (findBy === 'restaurant') {
      setIsRestaurantDailyWasteReportLoading(isTrue);
    } else if (findBy === 'location') {
      setIsLocationDailyWasteReportLoading(isTrue);
    }
  };

  /**
   * This function handles the generation of aggregated daily service waste data csv file for download.
   * During the process, there is a check to stop the function when isGroupDailyWasteReportCancelledRef.current, isPropertyDailyWasteReportCancelledRef.current or isLocationDailyWasteReportCancelledRef.current is false.
   */
  const generateDailyWasteReport = async (findBy: string) => {
    try {
      const {
        companyField,
        restaurantField,
        mapSetSelectedServiceIdBySelectedLocationId,
        dateRange,
      } = formState;
      const [fromDate, toDate] = dateRange;
      let id;
      let fileName;
      const companyFieldParsed = JSON.parse(companyField);
      const { isAirline, name: companyName, companyId, index: companyIndex } = companyFieldParsed;
      if (isAirline) {
        setIsAlert(true);
        setAlertHeader('Invalid Group Selected');
        setArrAlertMessage([
          `Only non-airline group can be selected for Daily Waste CSV file download`,
        ]);
        return;
      }
      setIsDailyWasteReportLoading(findBy, true);

      if (findBy === 'company') {
        isGroupDailyWasteReportCancelledRef.current = false;
        groupDailyWasteReportController.current = new AbortController();
        id = companyId;
        fileName = `Group_${companyName}_${fromDate}_${toDate}_daily_waste_data`;
      } else if (findBy === 'restaurant') {
        isPropertyDailyWasteReportCancelledRef.current = false;
        propertyDailyWasteReportController.current = new AbortController();
        const { restaurantId, name: restaurantName } = JSON.parse(restaurantField);
        id = restaurantId;
        fileName = `Property_${companyName}_${restaurantName}_${fromDate}_${toDate}_daily_waste_data`;
      } else if (findBy === 'location') {
        isLocationDailyWasteReportCancelledRef.current = false;
        locationDailyWasteReportController.current = new AbortController();
        const companyService = arrValidCompanyService[companyIndex];
        const { name: restaurantName, index: restaurantIndex } = JSON.parse(restaurantField);
        // Only 1 location is selected to enable the location generateDailyWasteReport button
        const [[locationId]] = Array.from(mapSetSelectedServiceIdBySelectedLocationId.entries());
        const restaurantService = companyService.arrRestaurantService[restaurantIndex];
        const locationService = restaurantService.arrLocationService.find(
          (locationService) => locationService.locationId === locationId
        )!;
        id = locationId;
        fileName = `Location_${companyName}_${restaurantName}_${locationService.name}_${fromDate}_${toDate}_daily_waste_data`;
      }
      if (
        (findBy === 'company' && isGroupDailyWasteReportCancelledRef.current) ||
        (findBy === 'restaurant' && isPropertyDailyWasteReportCancelledRef.current) ||
        (findBy === 'location' && isLocationDailyWasteReportCancelledRef.current)
      )
        return;
      const startDate = fromDate.format('YYYY-MM-DD');
      const endDate = toDate.format('YYYY-MM-DD');
      let targetAbortController;
      if (findBy === 'company') {
        targetAbortController = groupDailyWasteReportController.current;
      } else if (findBy === 'restaurant') {
        targetAbortController = propertyDailyWasteReportController.current;
      } else {
        targetAbortController = locationDailyWasteReportController.current;
      }
      const response = await axios.post(
        '/report-management/fetch-daily-waste-report',
        {
          findBy,
          attribute: {
            id,
            startDate,
            endDate,
          },
        },
        {
          signal: targetAbortController?.signal,
        }
      );

      const { arrReport } = response.data;
      if (
        (findBy === 'company' && isGroupDailyWasteReportCancelledRef.current) ||
        (findBy === 'restaurant' && isPropertyDailyWasteReportCancelledRef.current) ||
        (findBy === 'location' && isLocationDailyWasteReportCancelledRef.current)
      )
        return;
      // If returned arrReport has length 0, display 'no data' error
      if (arrReport.length === 0) {
        displayError(ERROR_MESSAGE.noDataInDateRange, ERROR_TYPE.fatal);
        setIsDailyWasteReportLoading(findBy, false);
        return;
      }
      if (
        (findBy === 'company' && isGroupDailyWasteReportCancelledRef.current) ||
        (findBy === 'restaurant' && isPropertyDailyWasteReportCancelledRef.current) ||
        (findBy === 'location' && isLocationDailyWasteReportCancelledRef.current)
      )
        return;
      const options = {
        filename: fileName,
        quoteStrings: '',
        showLabels: true,
        useKeysAsHeaders: true,
      };
      const csvExporter = new ExportToCsv(options);
      csvExporter.generateCsv(arrReport);
    } catch (error: any) {
      if (!(axios.isCancel(error) || error.name === 'CanceledError')) {
        displayError(ERROR_MESSAGE.fetchReportError, ERROR_TYPE.fatal);
      }
    } finally {
      setIsDailyWasteReportLoading(findBy, false);
    }
  };

  /**
   * Whatsapp message caption assembled using count of issues and if there are zero wastes captured
   * @param locationDateString - Location name and date of throw in string included in caption header
   * @param arrDateTopIncorrectSmartStartSignedUrl - Array of incorrect smart start detail
   * @param arrDateTopWasteDetailByService - Array of top waste detail including duplicate, mixed and usage issue
   * @returns whatsappCaption - Caption attached to image to be sent via whatsapp
   */
  const getWhatsappCaption = (
    locationDateString: string,
    arrDateTopIncorrectSmartStartSignedUrl: DateTopWasteImageSignedUrlByWeight[],
    arrDateTopWasteDetailByService: Array<DateTopWasteDetailByService>
  ) => {
    let whatsappCaption = `Good day team! Here is the summary of top wastes for ${locationDateString}`;

    if (arrDateTopIncorrectSmartStartSignedUrl.length > 0) {
      const isMultiDayPropertyTopWaste = !!arrDateTopIncorrectSmartStartSignedUrl[0].date;
      whatsappCaption += `

There were smart start error(s) found`;
      if (isMultiDayPropertyTopWaste) {
        const setDate = new Set(arrDateTopIncorrectSmartStartSignedUrl.map(({ date }) => date));
        whatsappCaption += `:`;
        setDate.forEach((date) => {
          whatsappCaption += `
${date}`;
        });
      } else {
        whatsappCaption += '.';
      }
      whatsappCaption += `
Kindly make sure that the blue weight bucket is *installed and completely empty* before starting up the device.`;
    }

    const arrDuplicateString: Array<string> = [];
    const arrMixedString: Array<string> = [];
    const arrUsageIssueString: Array<string> = [];
    let isZeroThrow = true;
    arrDateTopWasteDetailByService?.forEach(
      ({
        arrDateTopDuplicateWasteImageSignedUrlByWeight,
        arrDateTopMixedWasteImageSignedUrlByWeight,
        arrDateTopUsageWasteImageSignedUrlByWeight,
        arrDateTopWasteImageSignedUrlByWeight,
        serviceName,
      }) => {
        arrDateTopDuplicateWasteImageSignedUrlByWeight.forEach(({ date }) => {
          arrDuplicateString.push(`${serviceName}${date ? ` (${date})` : ''}`);
        });
        arrDateTopMixedWasteImageSignedUrlByWeight.forEach(({ date }) => {
          arrMixedString.push(`${serviceName}${date ? ` (${date})` : ''}`);
        });
        arrDateTopUsageWasteImageSignedUrlByWeight.forEach(({ date }) => {
          arrUsageIssueString.push(`${serviceName}${date ? ` (${date})` : ''}`);
        });
        if (isZeroThrow) {
          isZeroThrow = arrDateTopWasteImageSignedUrlByWeight.length === 0;
        }
      }
    );

    if (arrDuplicateString.length > 0) {
      whatsappCaption += `

There were duplicate throw(s) found:`;
      new Set(arrDuplicateString).forEach((duplicateString) => {
        whatsappCaption += `
${duplicateString}`;
      });
      whatsappCaption += `
Please ensure the green button is *pressed only once* when the food item is ready to be captured.`;
    }

    if (arrMixedString.length > 0) {
      whatsappCaption += `

There were mixed throw(s) found:`;
      new Set(arrMixedString).forEach((mixedString) => {
        whatsappCaption += `
${mixedString}`;
      });
      whatsappCaption += `
Please ensure to *separate food items* before throwing them for proper recording of your waste.`;
    }

    if (arrUsageIssueString.length > 0) {
      whatsappCaption += `

There were usage issue(s) found:`;
      new Set(arrUsageIssueString).forEach((usageIssueString) => {
        whatsappCaption += `
${usageIssueString}`;
      });
      whatsappCaption += `
Kindly make sure you *put all the food waste first* before pressing the green button *to avoid* any utensils/hands *blocking* the camera.`;
    }

    if (isZeroThrow) {
      whatsappCaption += `

    There were *no throws recorded* for this date. Was this expected?`;
    }

    whatsappCaption += `

Thank you!`;

    return whatsappCaption;
  };

  /**
   * This function reads the referenced component from the DOM and converts the html to a canvas to be
   * downloaded as a jpeg by the user
   * @param fileName - Name of file to be downloaded
   * @param isPropertyTopWasteCancelledRef - React reference that stores the boolean of whether the download is to be cancelled
   * @param isToBeSentViaWhatsapp - Boolean of whether to send the generate csv to whatsapp instead of downloading
   * @param locationDateString - Location name and date of throw in string included in caption header
   * @param arrDateTopIncorrectSmartStartSignedUrl - Array of incorrect smart start detail
   * @param arrDateTopWasteDetailByService - Array of top waste detail including duplicate, mixed and usage issue
   */
  const initiatePropertyTopWasteImageDownload = async (
    fileName: string,
    isPropertyTopWasteCancelledRef: React.MutableRefObject<boolean>,
    isToBeSentViaWhatsapp?: boolean,
    locationDateString?: string,
    arrDateTopIncorrectSmartStartSignedUrl?: Array<DateTopWasteImageSignedUrlByWeight>,
    arrDateTopWasteDetailByService?: Array<DateTopWasteDetailByService>
  ) => {
    const elementToBeDownloaded = propertyTopWasteRef.current;
    const canvas = await html2canvas(elementToBeDownloaded!, { useCORS: true, scale: 2 });
    if (isPropertyTopWasteCancelledRef.current) return;
    const data = canvas.toDataURL('image/jpeg');
    if (isPropertyTopWasteCancelledRef.current) return;
    if (isToBeSentViaWhatsapp) {
      const caption = getWhatsappCaption(
        locationDateString!,
        arrDateTopIncorrectSmartStartSignedUrl!,
        arrDateTopWasteDetailByService!
      );
      try {
        await axios.post('/report-management/send-whatsapp-image', {
          whatsappGroupName: JSON.parse(formState.restaurantField).whatsappGroupName,
          session: whatsappMessagingSessionSelected,
          fileName: `${fileName}.jpg`,
          imageUrl: data,
          caption,
        });
      } catch (error) {
        // On whatsapp image send error, download image instead
        const link = document.createElement('a');
        link.href = data;
        link.download = `${fileName}.jpg`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        throw new Error(WHATSAPP_CONNECTION_ERROR);
      }
    } else {
      const link = document.createElement('a');
      link.href = data;
      link.download = `${fileName}.jpg`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  /**
   * This function handles the fetching and generation of property top wastes to be downloaded
   * If property top wastes are to be combined, then property top wastes are consolidated into a single date range instead of
   * multiple days within the date range
   * @param isToBeCombined - Boolean to combine all dates in date range or remain separated by each day in date range
   * @param isToBeSentViaWhatsapp - Boolean of whether to send the generate csv to whatsapp instead of downloading
   */
  const downloadPropertyTopWaste = async (
    isToBeCombined: boolean,
    isToBeSentViaWhatsapp?: boolean
  ) => {
    try {
      if (isToBeCombined) {
        setIsMultiDayPropertyTopWasteLoading(true);
        isMultiDayPropertyTopWasteCancelledRef.current = false;
        multiDayPropertyTopWasteController.current = new AbortController();
      } else {
        setIsSingleDayPropertyTopWasteLoading(true);
        isSingleDayPropertyTopWasteCancelledRef.current = false;
        singleDayPropertyTopWasteController.current = new AbortController();
      }
      const {
        dateRange,
        restaurantField,
        companyField,
        mapSetSelectedServiceIdBySelectedLocationId,
        includeIssueSlides,
      } = formState;
      const [fromDate, toDate] = dateRange;
      const startDate = fromDate.format('YYYY-MM-DD');
      const endDate = toDate.format('YYYY-MM-DD');
      const endDateAfter7Days = DateTime.fromISO(startDate)
        .plus({ days: 6 })
        .toFormat('yyyy-MM-dd');
      const companyFieldParsed = JSON.parse(companyField);
      const restaurantFieldParsed = JSON.parse(restaurantField);
      const companyService = arrValidCompanyService[Number(companyFieldParsed.index)];
      const restaurantService =
        companyService.arrRestaurantService[Number(restaurantFieldParsed.index)];
      const arrLocationServiceToFetchPropertyTopWaste: LocationService[] = [];
      restaurantService.arrLocationService.forEach((locationService) => {
        const { locationId, arrService } = locationService;
        if (mapSetSelectedServiceIdBySelectedLocationId.has(locationId)) {
          const setSelectedServiceId = mapSetSelectedServiceIdBySelectedLocationId.get(locationId);
          const arrServiceSelected = arrService.filter((service) =>
            setSelectedServiceId!.has(service.serviceId)
          );
          sortByNamePriority(arrServiceSelected);
          arrLocationServiceToFetchPropertyTopWaste.push({
            ...locationService,
            arrService: arrServiceSelected,
          });
        }
      });
      const restaurantServiceToFetchPropertyTopWaste = {
        ...restaurantService,
        arrLocationService: arrLocationServiceToFetchPropertyTopWaste,
      };
      const request = {
        restaurantService: restaurantServiceToFetchPropertyTopWaste,
        startDate,
        endDate: isToBeCombined
          ? endDate
          : endDate > endDateAfter7Days
          ? endDateAfter7Days
          : endDate,
        includeIssue: includeIssueSlides,
        isToBeCombined,
      };
      const response = await axios.post('/report-management/fetch-property-top-waste', request, {
        signal: isToBeCombined
          ? multiDayPropertyTopWasteController?.current?.signal
          : singleDayPropertyTopWasteController?.current?.signal,
      });
      const { arrLocationServiceTopWasteDetail } = response.data;
      // For loop used due to requirement to await DOM changes and downloading of each location's top waste images
      for (
        let locationIndex = 0;
        locationIndex < arrLocationServiceTopWasteDetail.length;
        locationIndex += 1
      ) {
        const { locationName, arrTopWasteDetailByDateByService } =
          arrLocationServiceTopWasteDetail[locationIndex];
        for (
          let serviceIndex = 0;
          serviceIndex < arrTopWasteDetailByDateByService.length;
          serviceIndex += 1
        ) {
          if (
            (isToBeCombined && isMultiDayPropertyTopWasteCancelledRef.current) ||
            (!isToBeCombined && isSingleDayPropertyTopWasteCancelledRef.current)
          )
            return;
          const { date, arrDateTopIncorrectSmartStartSignedUrl, arrDateTopWasteDetailByService } =
            arrTopWasteDetailByDateByService[serviceIndex];
          setDataToDisplay({
            locationName,
            date,
            arrDateTopIncorrectSmartStartSignedUrl,
            arrDateTopWasteDetailByService,
          });
          // setTimeout required for setState above to take effect on the DOM before initiating image download
          // This is required due to html2canvas implementation that REQUIRES rendering on the dom before a "screenshot"
          // is captured to be downloaded to the user
          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (
                (isToBeCombined && isMultiDayPropertyTopWasteCancelledRef.current) ||
                (!isToBeCombined && isSingleDayPropertyTopWasteCancelledRef.current)
              ) {
                reject(new Error('Canceled download by user'));
              } else {
                resolve();
              }
            }, 500);
          });
          if (
            (isToBeCombined && isMultiDayPropertyTopWasteCancelledRef.current) ||
            (!isToBeCombined && isSingleDayPropertyTopWasteCancelledRef.current)
          )
            return;
          await initiatePropertyTopWasteImageDownload(
            `${locationName}_${arrLocationServiceTopWasteDetail[locationIndex].arrTopWasteDetailByDateByService[serviceIndex].date}`,
            isToBeCombined
              ? isMultiDayPropertyTopWasteCancelledRef
              : isSingleDayPropertyTopWasteCancelledRef,
            isToBeSentViaWhatsapp,
            `${locationName} (${date})`,
            arrDateTopIncorrectSmartStartSignedUrl,
            arrDateTopWasteDetailByService
          );
          if (
            (isToBeCombined && isMultiDayPropertyTopWasteCancelledRef.current) ||
            (!isToBeCombined && isSingleDayPropertyTopWasteCancelledRef.current)
          )
            return;
        }
      }
      if (!isToBeCombined && endDate > endDateAfter7Days)
        displayError(ERROR_MESSAGE.dateChosenExceedSevenDaysError, ERROR_TYPE.warning);
    } catch (error: any) {
      if (
        !(
          axios.isCancel(error) ||
          error.name === 'CanceledError' ||
          error.message === 'Canceled download by user'
        )
      ) {
        if (error?.message === WHATSAPP_CONNECTION_ERROR) {
          displayError(ERROR_MESSAGE.whatsappConnectionError, ERROR_TYPE.fatal);
        } else {
          displayError(ERROR_MESSAGE.fetchPropertyTopWasteError, ERROR_TYPE.fatal);
        }
      }
    } finally {
      setDataToDisplay(null);
      if (isToBeCombined) {
        setIsMultiDayPropertyTopWasteLoading(false);
      } else {
        setIsSingleDayPropertyTopWasteLoading(false);
      }
    }
  };

  /**
   * This function handles the images' signed url returned for download
   * Each signed url returns an image in 'blob' format which is accepted by the JSZip API
   * The zip file is then generated and downloaded by the user via saveAs
   * During the process, there is a check to stop the function when isServiceImagesCancelledRef.current is false or stop the axios request when there is abort signal is detected.
   */
  const downloadImage = async () => {
    const { mapSetSelectedServiceIdBySelectedLocationId, isTagged, dateRange } = formState;
    const [fromDate, toDate] = dateRange;
    const startDate = fromDate.format('YYYY-MM-DD');
    const endDate = toDate.format('YYYY-MM-DD');
    // Only 1 location is selected to enable the downloadImage button
    const [[locationId, setSelectedServiceId]] = Array.from(
      mapSetSelectedServiceIdBySelectedLocationId.entries()
    );
    const arrServiceId = Array.from(setSelectedServiceId);
    const request = {
      arrServiceId,
      startDate,
      endDate,
      isTagged,
    };
    try {
      setIsImageLoading(true);
      isServiceImagesCancelledRef.current = false;
      serviceImagesController.current = new AbortController();
      const response = await axios.post('/report-management/fetch-image-signed-url', request, {
        signal: serviceImagesController.current.signal,
      });
      const { arrImageSignedUrl, arrDownloadError } = response.data;
      const hasImageSignedUrl = arrImageSignedUrl.length > 0;
      const hasDownloadError = arrDownloadError.length > 0;

      // If returned hasImageSignedUrl and arrDownloadError both has length 0, display 'no image' error
      if (!hasImageSignedUrl && !hasDownloadError) {
        displayError(ERROR_MESSAGE.noImageInDateRange, ERROR_TYPE.fatal);
        return;
      }

      // If returned hasImageSignedUrl has images to be downloaded, package them into a zip file and ship to user
      if (hasImageSignedUrl) {
        const { companyField, restaurantField } = formState;
        const companyFieldParsed = JSON.parse(companyField);
        const restaurantFieldParsed = JSON.parse(restaurantField);
        const companyService = arrValidCompanyService[Number(companyFieldParsed.index)];
        const restaurantService =
          companyService.arrRestaurantService[Number(restaurantFieldParsed.index)];
        const locationService = restaurantService.arrLocationService.find(
          (locationService) => locationService.locationId === locationId
        )!;
        const zip = new JSZip();
        await Promise.all(
          arrImageSignedUrl.map(async (imageSignedUrl: { name: string; signedUrl: string }) => {
            const imageResponse = await axios.get(imageSignedUrl.signedUrl, {
              responseType: 'blob',
              signal: serviceImagesController?.current?.signal,
            });
            const image = imageResponse.data;
            zip.file(imageSignedUrl.name, image);
          })
        );
        const zipFile = await zip.generateAsync({ type: 'blob' }, () => {
          if (isServiceImagesCancelledRef.current) {
            throw new Error('Canceled download by user');
          }
        });
        const fileName = `${JSON.parse(companyField).name}_${JSON.parse(restaurantField).name}_${
          locationService.name
        }_${locationService.arrService.map((service) => service.name).join('_')}_${
          isTagged === undefined ? 'All' : isTagged === false ? 'Untagged' : 'Tagged'
        }_${fromDate}_${toDate}`;
        saveAs(zipFile, fileName);
      }
      if (isServiceImagesCancelledRef.current) return;
      // If returned arrDownloadError has images unable to be downloaded, notify user to notify software team
      // Count of download errors are displayed out of total images
      // If there are a mix of valid images and download errors, valid images are downloaded and users are notified of its count
      if (hasDownloadError) {
        setIsAlert(true);
        setAlertHeader('Invalid Download(s) Exist(s)');
        setArrAlertMessage([
          `Of the service's ${
            arrImageSignedUrl.length + arrDownloadError.length
          } image downloads, ${arrDownloadError.length} had returned errors.`,
          `${
            hasImageSignedUrl
              ? `Remaining ${arrImageSignedUrl.length} images have been downloaded.`
              : ''
          }`,
          'Please screenshot this alert, and notify software team immediately.',
          `Datetime: ${new Date().toLocaleString()}`,
        ]);
      }
    } catch (error: any) {
      if (
        !(
          axios.isCancel(error) ||
          error.name === 'CanceledError' ||
          error.message === 'Canceled download by user'
        )
      ) {
        displayError(ERROR_MESSAGE.fetchImageError, ERROR_TYPE.fatal);
      }
    } finally {
      setIsImageLoading(false);
    }
  };

  /**
   * This function handles the backend request for '/report-management/fetch-graph'
   * Waste images are returned as signed url due to Google App Engine's max 32mb response limit, hence image fetching
   * has to be done in the frontend. Graph images are returned as base64 strings as they are constructed in the backend
   * and are relatively much lower in size (~50kb per graph image).
   * @returns Object.arrLocationData - Array of location Waste analysis for the property
   * @returns Object.fileName - Name of file to be downloaded
   * @returns Object.restaurantService - Restaurant and locationService of graphs fetched for
   */
  const fetchGraph = async () => {
    const {
      dateRange,
      companyField,
      restaurantField,
      mapSetSelectedServiceIdBySelectedLocationId,
      isTagged,
    } = formState;
    const [fromDate, toDate] = dateRange;
    const startDate = fromDate.format('YYYY-MM-DD');
    const endDate = toDate.format('YYYY-MM-DD');
    const companyFieldParsed = JSON.parse(companyField);
    const restaurantFieldParsed = JSON.parse(restaurantField);
    const companyService = arrValidCompanyService[Number(companyFieldParsed.index)];
    const restaurantService =
      companyService.arrRestaurantService[Number(restaurantFieldParsed.index)];
    const arrLocationServiceToFetchGraph: LocationService[] = [];
    restaurantService.arrLocationService.forEach((locationService) => {
      const { locationId, arrService } = locationService;
      if (mapSetSelectedServiceIdBySelectedLocationId.has(locationId)) {
        const setSelectedServiceId = mapSetSelectedServiceIdBySelectedLocationId.get(locationId);
        const arrServiceSelected = arrService.filter((service) =>
          setSelectedServiceId!.has(service.serviceId)
        );
        sortByNamePriority(arrServiceSelected);
        arrLocationServiceToFetchGraph.push({
          ...locationService,
          arrService: arrServiceSelected,
        });
      }
    });
    const restaurantServiceToFetchGraph = {
      ...restaurantService,
      arrLocationService: arrLocationServiceToFetchGraph,
    };
    const request = {
      companyName: companyFieldParsed.name,
      restaurantService: restaurantServiceToFetchGraph,
      startDate,
      endDate,
      isTagged,
    };
    const response = await axios.post('/report-management/fetch-graph', request, {
      signal: propertyMonthlyReviewController?.current?.signal,
    });
    return {
      ...response.data,
      fileName: `${restaurantFieldParsed.name}_${fromDate}_${toDate}`,
      restaurantService: restaurantServiceToFetchGraph,
    };
  };

  /**
   * This function handles the backend request for '/report-management/fetch-baseline-graph'
   * Waste images are returned as signed url due to Google App Engine's max 32mb response limit, hence image fetching
   * has to be done in the frontend. Graph images are returned as base64 strings as they are constructed in the backend
   * and are relatively much lower in size (~50kb per graph image).
   * @returns Object.baselineReportData - Object containing key: locationName, value: locationData
   * @returns Object.fileName - Name of file to be downloaded
   * @returns Object.restaurantService - Restaurant, location and arrService of graphs fetched for
   */
  const fetchBaselineGraph = async () => {
    const {
      dateRange,
      companyField,
      restaurantField,
      mapSetSelectedServiceIdBySelectedLocationId,
      isTagged,
    } = formState;
    const [fromDate, toDate] = dateRange;
    const startDate = fromDate.format('YYYY-MM-DD');
    const endDate = toDate.format('YYYY-MM-DD');
    const companyFieldParsed = JSON.parse(companyField);
    const restaurantFieldParsed = JSON.parse(restaurantField);
    // Only 1 location is selected to enable the baseline report button
    const [[locationId, setSelectedServiceId]] = Array.from(
      mapSetSelectedServiceIdBySelectedLocationId.entries()
    );
    const companyService = arrValidCompanyService[Number(companyFieldParsed.index)];
    const restaurantService =
      companyService.arrRestaurantService[Number(restaurantFieldParsed.index)];
    const locationService = restaurantService.arrLocationService.find(
      (locationService) => locationService.locationId === locationId
    )!;
    const restaurantServiceToFetchBaselineGraph = {
      ...restaurantService,
      arrLocationService: [
        {
          ...locationService,
          arrService: locationService!.arrService.filter((service) =>
            setSelectedServiceId.has(service.serviceId)
          ),
        },
      ],
    };
    const request = {
      companyName: companyFieldParsed.name,
      restaurantService: restaurantServiceToFetchBaselineGraph,
      startDate,
      endDate,
      isTagged,
    };
    const response = await axios.post('/report-management/fetch-baseline-graph', request, {
      signal: baselineReviewController?.current?.signal,
    });
    return {
      ...response.data,
      fileName: `Baseline_Report_For_${restaurantFieldParsed.name}_${locationService.name}_${fromDate}_${toDate}`,
      restaurantService: restaurantServiceToFetchBaselineGraph,
    };
  };

  /**
   * This function is invoked on change of Company select element to update formState and the restaurantService
   * selection(s)
   * @param event - Change event provided by the select element, provides companyId, companyName, isAirline
   */
  const updateCompanyFormState = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFormState({
      ...formState,
      companyField: event.target.value,
      restaurantField: 'Select Property',
      mapSetSelectedServiceIdBySelectedLocationId: new Map(),
    });
    setMinDate(undefined);
    resetErrorMessage();
  };

  /**
   * This function is invoked on change date range picker to update formState
   * @param dateRange - Array containing from and to DateObjects
   */
  const updateDateRangeFormState = (dateRange: Array<DateObject>) => {
    setFormState({ ...formState, dateRange });
    resetErrorMessage();
  };

  /**
   * This function is invoked on change of Restaurant select element to update formState
   * @param event - Change event providing restaurantName, restaurant's index
   */
  const updateRestaurantFormState = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const updatedFormState = {
      ...formState,
      restaurantField: event.target.value,
      mapSetSelectedServiceIdBySelectedLocationId: new Map(),
    };
    const companyFieldParsed = JSON.parse(updatedFormState.companyField);
    const restaurantFieldParsed = JSON.parse(updatedFormState.restaurantField);
    const companyService = arrValidCompanyService[Number(companyFieldParsed.index)];
    const restaurantService =
      companyService.arrRestaurantService[Number(restaurantFieldParsed.index)];
    const { propertyEarliestStartDate, propertyEarliestProjectStartDate } =
      getPropertyEarliestStartDateAndProjectStartDate(restaurantService);
    if (propertyEarliestProjectStartDate !== null) {
      const propertyEarliestProjectStartDateInDateObjectFormat = new DateObject(
        propertyEarliestProjectStartDate
      );
      setMinDate(propertyEarliestProjectStartDateInDateObjectFormat);
      // If min date is later than currently selected from date, then set from date as min date
      if (propertyEarliestProjectStartDate > formState.dateRange[0].format('YYYY-MM-DD')) {
        updatedFormState.dateRange = [
          propertyEarliestProjectStartDateInDateObjectFormat,
          formState.dateRange[1],
        ];
      }
      // If min date is later than currently selected to date, then set to date as min date
      if (propertyEarliestProjectStartDate > formState.dateRange[1].format('YYYY-MM-DD')) {
        updatedFormState.dateRange[1] = propertyEarliestProjectStartDateInDateObjectFormat;
      }
    } else {
      setMinDate(undefined);
    }
    setMinStartDate(
      propertyEarliestStartDate === null ? undefined : new DateObject(propertyEarliestStartDate)
    );
    setFormState(updatedFormState);
    resetErrorMessage();
  };

  /**
   * This function retrieves a service's latest start date from its baseline dates
   * @param arrServiceBaselineDate - Array of baseline dates
   * @returns latestStartDate - latest start date of service baseline dates
   */
  const getServiceLatestStartDateFromArrServiceBaselineDate = (
    arrServiceBaselineDate: Array<ServiceBaselineDate>
  ): null | string => {
    let latestStartDate: null | string = null;
    arrServiceBaselineDate.forEach(({ startDate }) => {
      if (latestStartDate === null || startDate > latestStartDate) {
        latestStartDate = startDate;
      }
    });
    return latestStartDate;
  };

  /**
   * This function retrieves the selected property's earliest project and baseline start date among its valid services
   * @param restaurantService - Restaurant service to get the earliest project and baseline start date of the property
   * @returns propertyEarliestStartDate - Property's earliest baseline start date among its valid services
   * @returns propertyEarliestProjectStartDate - Property's earliest project start date among its valid services
   */
  const getPropertyEarliestStartDateAndProjectStartDate = (
    restaurantService: RestaurantService
  ): {
    propertyEarliestStartDate: string | null;
    propertyEarliestProjectStartDate: string | null;
  } => {
    let propertyEarliestStartDate: string | null = null;
    let propertyEarliestProjectStartDate: string | null = null;
    restaurantService.arrLocationService.forEach((locationService) => {
      locationService.arrService.forEach((service) => {
        const { projectStartDate, arrServiceBaselineDate, projectEndDate, serviceType } = service;
        if (
          projectEndDate === null &&
          arrServiceBaselineDate.length > 0 &&
          serviceType.type !== 'Placeholder'
        ) {
          if (
            propertyEarliestProjectStartDate === null ||
            projectStartDate < propertyEarliestProjectStartDate
          ) {
            propertyEarliestProjectStartDate = projectStartDate;
          }

          const serviceLatestStartDate =
            getServiceLatestStartDateFromArrServiceBaselineDate(arrServiceBaselineDate);

          if (
            propertyEarliestStartDate === null ||
            (serviceLatestStartDate !== null && serviceLatestStartDate < propertyEarliestStartDate)
          ) {
            propertyEarliestStartDate = serviceLatestStartDate;
          }
        }
      });
    });
    return { propertyEarliestStartDate, propertyEarliestProjectStartDate };
  };

  /**
   * This function is invoked on clicking a selection of company, restaurant and location on the SearchBar component.
   * With the selection of a location, the date picker will also implement a min date selectable by the user according to
   * the property's earliest project start date.
   * If the currently selected date range falls before partially or fully of the property's earliest project start date,
   * then the selected 'from' date will be updated to the property's earliest project start date.
   * @param event - Mouse event
   * @param selectedOption - Selected companyLocation option providing companyField, restaurantField and mapSetSelectedServiceIdByLocationId information
   */
  const updateCompanyLocationFormState = (
    event: React.MouseEvent<HTMLLIElement>,
    selectedOption: CompanyLocationOption
  ) => {
    const {
      companyId,
      companyName,
      isAirline,
      companyIndex,
      restaurantId,
      restaurantName,
      restaurantIndex,
      locationId,
      locationIndex,
      whatsappGroupName,
    } = selectedOption;
    const companyService = arrValidCompanyService[companyIndex];

    const selectedCompanyField = `{"companyId": ${companyId}, "name": "${companyName}", "isAirline": ${isAirline}, "index": ${companyIndex}}`;
    const selectedRestaurantField = `{"name": "${restaurantName}", "restaurantId": ${restaurantId}, "index": ${restaurantIndex}, "whatsappGroupName": "${whatsappGroupName}"}`;

    // Retrieve arrService in order to get property's earliest project start date
    const restaurantService = companyService.arrRestaurantService[restaurantIndex];
    const { arrService } = restaurantService.arrLocationService[locationIndex];
    const { propertyEarliestStartDate, propertyEarliestProjectStartDate } =
      getPropertyEarliestStartDateAndProjectStartDate(restaurantService);
    const updatedFormState = {
      ...formState,
      companyField: selectedCompanyField,
      restaurantField: selectedRestaurantField,
      mapSetSelectedServiceIdBySelectedLocationId:
        arrService.length !== 0
          ? new Map([[locationId, new Set(arrService.map((service) => service.serviceId))]])
          : new Map(),
    };

    if (propertyEarliestProjectStartDate !== null) {
      const propertyEarliestProjectStartDateInDateObjectFormat = new DateObject(
        propertyEarliestProjectStartDate
      );
      setMinDate(propertyEarliestProjectStartDateInDateObjectFormat);
      // If min date is later than currently selected from date, then set from date as min date
      if (propertyEarliestProjectStartDate > formState.dateRange[0].format('YYYY-MM-DD')) {
        updatedFormState.dateRange = [
          propertyEarliestProjectStartDateInDateObjectFormat,
          formState.dateRange[1],
        ];
      }
      // If min date is later than currently selected to date, then set to date as min date
      if (propertyEarliestProjectStartDate > formState.dateRange[1].format('YYYY-MM-DD')) {
        updatedFormState.dateRange[1] = propertyEarliestProjectStartDateInDateObjectFormat;
      }
    } else {
      setMinDate(undefined);
    }
    setMinStartDate(
      propertyEarliestStartDate === null ? undefined : new DateObject(propertyEarliestStartDate)
    );
    setFormState(updatedFormState);
    resetErrorMessage();
  };

  /**
   * This function is invoked on change of Location checkbox element to update formState
   * @property isChecked - Indicates if the checkbox is checked or unchecked
   * @property locationService - Location with its array of valid services, where its location checkbox is checked or unchecked
   */
  const updateLocationServiceFormState = (isChecked: boolean, locationService: LocationService) => {
    const { locationId, arrService } = locationService;
    const { mapSetSelectedServiceIdBySelectedLocationId } = formState;
    const updatedMapSetSelectedServiceIdBySelectedLocationId = new Map(
      mapSetSelectedServiceIdBySelectedLocationId
    );
    if (isChecked) {
      updatedMapSetSelectedServiceIdBySelectedLocationId.set(
        locationId,
        new Set(arrService.map((service) => service.serviceId))
      );
    } else {
      updatedMapSetSelectedServiceIdBySelectedLocationId.delete(locationId);
    }
    const updatedFormState = {
      ...formState,
      mapSetSelectedServiceIdBySelectedLocationId:
        updatedMapSetSelectedServiceIdBySelectedLocationId,
    };

    setFormState(updatedFormState);
    resetErrorMessage();
  };

  /**
   * This function is invoked on change of Service checkbox element to update formState
   * @property isChecked - Indicates if the checkbox is checked or unchecked
   * @property service -Service where its service checkbox is checked or unchecked
   */
  const updateServiceFormState = (isChecked: boolean, service: Service) => {
    const { locationId, serviceId } = service;
    const { mapSetSelectedServiceIdBySelectedLocationId } = formState;
    const updatedMapSetSelectedServiceIdBySelectedLocationId = new Map(
      mapSetSelectedServiceIdBySelectedLocationId
    );
    if (isChecked) {
      if (updatedMapSetSelectedServiceIdBySelectedLocationId.has(locationId)) {
        updatedMapSetSelectedServiceIdBySelectedLocationId.get(locationId)!.add(serviceId);
      } else {
        updatedMapSetSelectedServiceIdBySelectedLocationId.set(locationId, new Set([serviceId]));
      }
    } else {
      if (updatedMapSetSelectedServiceIdBySelectedLocationId.get(locationId)!.size === 1) {
        updatedMapSetSelectedServiceIdBySelectedLocationId.delete(locationId);
      } else {
        updatedMapSetSelectedServiceIdBySelectedLocationId.get(locationId)!.delete(serviceId);
      }
    }
    const updatedFormState = {
      ...formState,
      mapSetSelectedServiceIdBySelectedLocationId:
        updatedMapSetSelectedServiceIdBySelectedLocationId,
    };

    setFormState(updatedFormState);
    resetErrorMessage();
  };

  /**
   * This function is invoked on change of isTagged radio element to update formState
   * @param isTagged - undefined (all), 'allExceptUntagged' (allExceptUntagged), 'taggedAndResolvable' (taggedAndResolvable), true (taggedAndMapped) or false (untagged)
   */
  const updateIsTaggedFormState = (isTagged: boolean | string | undefined) => {
    setFormState({ ...formState, isTagged });
  };

  /**
   * This function is invoked on change of includeIssueSlides checkbox component to update formState
   * @param includeIssueSlides - Indicates if issue waste analysis slides is to be included
   */
  const updateIncludeIssueSlidesFormState = (includeIssueSlides: boolean) => {
    setFormState({ ...formState, includeIssueSlides });
  };

  /**
   * This function is invoked on change of includeMenuItemName checkbox component to update formState
   * @param includeMenuItemName - Indicates if menu item name is to be included on the waste image
   */
  const updateIncludeMenuItemNameFormState = (includeMenuItemName: boolean) => {
    setFormState({ ...formState, includeMenuItemName });
  };

  /**
   * This function is called when "Select property's earliest baseline start date" button is clicked to assist
   * users in selecting the earliest start date of services of property selected to the last day of the
   * previous month.
   * In order for this function to be invoked, a property must have been selected before which populates
   * the minDate state, which is the earliest start date of services of property selected.
   * If all property's services have null startDate (minDate remains as undefined), an alert will appear
   * prompting user to add startDate
   * @returns DateObject[] - Array of DateObject
   * @returns DateObject[0] - Starting date of date range
   * @returns DateObject[1] - Ending date of date range
   */
  const selectPropertyEarliestStartDate = (): Array<DateObject> | undefined => {
    if (minStartDate !== undefined) {
      return [new DateObject(minStartDate), lastDateOfPrevMonth];
    } else {
      setIsAlert(true);
      setAlertHeader('No service start date found!');
      setArrAlertMessage([
        'To use this feature, please ensure start date for services under this property has been added.',
      ]);
    }
  };

  /**
   * This function is called when user clicks on any of the date range quick select buttons:
   * 1. Yesterday
   * 2. Last Week
   * 3. Last Month
   * 4. Last 3 Months
   * 4. From Property's Earliest Start Date
   * @param dateRangeOption - Type of quick select the user clicks
   */
  const quickSelectDateRange = (dateRangeOption: string) => {
    let dateRange: Array<DateObject> | undefined;
    const todayMoment = moment();
    switch (dateRangeOption) {
      case QUICK_SELECT_OPTION.yesterday:
        dateRange = [
          new DateObject(todayMoment.clone().subtract(1, 'day').format('YYYY-MM-DD')),
          new DateObject(todayMoment.clone().subtract(1, 'day').format('YYYY-MM-DD')),
        ];
        break;
      case QUICK_SELECT_OPTION.lastWeek:
        dateRange = [
          new DateObject(
            todayMoment.clone().subtract(1, 'week').startOf('isoWeek').format('YYYY-MM-DD')
          ),
          new DateObject(
            todayMoment.clone().subtract(1, 'week').endOf('isoWeek').format('YYYY-MM-DD')
          ),
        ];
        break;
      case QUICK_SELECT_OPTION.lastMonth:
        dateRange = [firstDateOfPrevMonth, lastDateOfPrevMonth];
        break;
      case QUICK_SELECT_OPTION.last3Months:
        dateRange = [
          new DateObject(
            todayMoment.clone().subtract(3, 'month').startOf('month').format('YYYY-MM-DD')
          ),
          new DateObject(
            todayMoment.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
          ),
        ];
        break;
      case QUICK_SELECT_OPTION.fromPropertyEarliestStartDate:
        dateRange = selectPropertyEarliestStartDate();
        break;
      default:
        return;
    }
    if (dateRange) {
      setFormState({
        ...formState,
        dateRange,
      });
    }
  };

  /**
   * This function resets the formState to its initial values
   */
  const resetForm = () => {
    setFormState(initialFormState);
    resetErrorMessage();
    setIsCompanyReportLoading(false);
    setIsRestaurantReportLoading(false);
    setIsLocationReportLoading(false);
    setIsImageLoading(false);
    setMinDate(undefined);
  };

  /**
   * Function cancels the download process. It aborts the network call, and stops the local processing. Due to the reason that some local processes take a long time, the function
   * also removes loader for the specific report, so that the user can immediately change the form inputs and download the report again.
   * Note: Although the download process seems to be cancelled upon clicking on the cross button, the frontend/backend's process is actually still ongoing, just that no download
   * will take place.
   * @param controllerRef - React reference that stores the AbortController object that signals the outgoing Axios requests to stop
   * @param isCancelledRef - React reference that stores the boolean of whether the download is to be cancelled
   * @param setIsLoading - React setState function uses to set the isLoading state to false
   */
  const cancelDownloadOnClick = (
    controllerRef: React.RefObject<null | AbortController>,
    isCancelledRef: React.MutableRefObject<boolean>,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (controllerRef.current) {
      controllerRef.current.abort(); // cancel network requests
    }
    isCancelledRef.current = true; // stop local processing
    setIsLoading(false);
  };

  /**
   * This function handles the generation of powerpoint slides and initiates download for the client browser.
   * During the process, there is a check to stop the function when isPropertyMonthlyReviewCancelledRef.current is false.
   */
  const downloadPropertyMonthlyReview = async () => {
    try {
      setIsPropertyMonthlyReviewLoading(true);
      isPropertyMonthlyReviewCancelledRef.current = false;
      propertyMonthlyReviewController.current = new AbortController();
      const slideImagesInBase64DataUrl = await getAllImageBase64Urls();
      const fetchGraphRes = await fetchGraph();
      const companyFieldParsed = JSON.parse(formState.companyField);
      const restaurantFieldParsed = JSON.parse(formState.restaurantField);
      const companyService = arrValidCompanyService[Number(companyFieldParsed.index)];
      const restaurantService =
        companyService.arrRestaurantService[Number(restaurantFieldParsed.index)];
      const propertyMonthlyReviewSlideMetadataResponse = await axios.post(
        '/report-management/fetch-property-monthly-review-slide-metadata',
        {
          restaurantId: restaurantService.restaurantId,
        },
        { signal: propertyMonthlyReviewController.current.signal }
      );
      if (isPropertyMonthlyReviewCancelledRef.current) return;
      const {
        defaultPropertyMonthlyReviewSlideMetadata,
        mapLocationNameByLocationId,
        mapServiceNameByServiceId,
      } = getDefaultPropertyMonthlyReviewSlideMetadata(
        arrValidCompanyService,
        restaurantService.restaurantId
      )!;
      let monthlyReviewSlideMetadata;
      if (
        propertyMonthlyReviewSlideMetadataResponse.data &&
        propertyMonthlyReviewSlideMetadataResponse.data.monthlyReviewSlideMetadata !== undefined
      ) {
        if (propertyMonthlyReviewSlideMetadataResponse.data.monthlyReviewSlideMetadata === null) {
          monthlyReviewSlideMetadata = defaultPropertyMonthlyReviewSlideMetadata;
        } else {
          monthlyReviewSlideMetadata =
            propertyMonthlyReviewSlideMetadataResponse.data.monthlyReviewSlideMetadata;
        }
      }
      if (isPropertyMonthlyReviewCancelledRef.current) return;
      await downloadPropertyMonthlyReviewPowerpoint(
        fetchGraphRes,
        monthlyReviewSlideMetadata,
        mapLocationNameByLocationId,
        mapServiceNameByServiceId,
        formState,
        user,
        slideImagesInBase64DataUrl,
        isPropertyMonthlyReviewCancelledRef
      );
      if (isPropertyMonthlyReviewCancelledRef.current) return;
      if (
        formState.includeIssueSlides &&
        fetchGraphRes.arrLocationData.some(
          (locationData: LocationMonthlyReportData) => locationData.hasNegativeWeight
        )
      ) {
        displayError(ERROR_MESSAGE.issueHasNegativeWeightWasteError, ERROR_TYPE.warning);
      }
    } catch (error: any) {
      if (!(axios.isCancel(error) || error.name === 'CanceledError')) {
        if (error?.response?.data.error === CALCULATOR_CONNECTION_ERROR) {
          displayError(ERROR_MESSAGE.calculatorConnectionError, ERROR_TYPE.fatal);
        } else {
          displayError(ERROR_MESSAGE.fetchPropertyMonthlyReviewError, ERROR_TYPE.fatal);
        }
      }
    } finally {
      setIsPropertyMonthlyReviewLoading(false);
    }
  };

  /**
   * This function handles the generation of powerpoint slides for baseline report and initiates download for the client browser.
   * During the process, there is a check to stop the function when isBaselineReviewCancelledRef.current is false.
   */
  const downloadBaselineReview = async () => {
    try {
      setIsBaselineReviewLoading(true);
      isBaselineReviewCancelledRef.current = false;
      baselineReviewController.current = new AbortController();
      const fetchBaselineGraphRes = await fetchBaselineGraph();
      if (isBaselineReviewCancelledRef.current) return;
      await downloadBaselineReviewPowerpoint(
        fetchBaselineGraphRes,
        formState,
        user,
        isBaselineReviewCancelledRef
      );
      if (isBaselineReviewCancelledRef.current) return;
      if (fetchBaselineGraphRes.baselineReportData.hasNegativeWeight) {
        displayError(ERROR_MESSAGE.issueHasNegativeWeightWasteError, ERROR_TYPE.warning);
      }
    } catch (error: any) {
      if (!(axios.isCancel(error) || error.name === 'CanceledError')) {
        displayError(ERROR_MESSAGE.fetchBaselineReviewError, ERROR_TYPE.fatal);
      }
    } finally {
      setIsBaselineReviewLoading(false);
    }
  };

  /**
   * This function handles the generation of excel spreadsheet for menu item mapping and initiates download for the client browser
   */
  const downloadPropertyMenuItemMapping = async () => {
    try {
      setIsPropertyMenuItemMappingDownloadLoading(true);
      isPropertyMenuItemMappingDownloadCancelledRef.current = false;
      propertyMenuItemMappingDownloadController.current = new AbortController();
      const { companyField, restaurantField, mapSetSelectedServiceIdBySelectedLocationId } =
        formState;
      const companyFieldParsed = JSON.parse(companyField);
      const { index: companyIndex } = companyFieldParsed;

      const companyService = arrValidCompanyService[companyIndex];
      const { index: restaurantIndex } = JSON.parse(restaurantField);
      const restaurantService = companyService.arrRestaurantService[restaurantIndex];

      const mapArrLocationServiceStringByMenuId = new Map();
      restaurantService.arrLocationService.forEach((locationService) => {
        const { locationId, arrService } = locationService;
        const setSelectedServiceId = mapSetSelectedServiceIdBySelectedLocationId.get(locationId);
        arrService.forEach((service) => {
          const { name: serviceName, menuId, serviceId } = service;
          if (setSelectedServiceId?.has(serviceId)) {
            const locationServiceString = `${locationService.name}>${serviceName}`;
            if (mapArrLocationServiceStringByMenuId.has(menuId)) {
              mapArrLocationServiceStringByMenuId.set(menuId, [
                ...mapArrLocationServiceStringByMenuId.get(menuId),
                locationServiceString,
              ]);
            } else {
              mapArrLocationServiceStringByMenuId.set(menuId, [locationServiceString]);
            }
          }
        });
      });

      const locationMenuItemMappingResponse = await axios.post(
        '/report-management/fetch-internal-menu-item-to-be-mapped',
        {
          arrMenuId: Array.from(mapArrLocationServiceStringByMenuId.keys()),
        },
        { signal: propertyMenuItemMappingDownloadController.current.signal }
      );
      if (
        locationMenuItemMappingResponse.data &&
        locationMenuItemMappingResponse.data
          .arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped !== undefined &&
        locationMenuItemMappingResponse.data.arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped
          .length > 0
      ) {
        await downloadPropertyMenuItemMappingSpreadsheet(
          locationMenuItemMappingResponse.data
            .arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped,
          mapArrLocationServiceStringByMenuId,
          restaurantService.name,
          propertyMenuItemMappingDownloadController,
          isPropertyMenuItemMappingDownloadCancelledRef
        );
      }
    } catch (error: any) {
      if (
        !(axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError')
      ) {
        displayError(ERROR_MESSAGE.fetchPropertyMenuItemMappingDownloadError, ERROR_TYPE.fatal);
      }
    } finally {
      setIsPropertyMenuItemMappingDownloadLoading(false);
    }
  };

  /**
   * This function handles the uploading of excel spreadsheet for menu item mapping and initiates menu item
   * mapping for the client browser
   * @param event - Change event from input element. Provides spreadsheet file uploaded by client
   */
  const uploadPropertyMenuItemMapping = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setIsPropertyMenuItemMappingUploadLoading(true);
      const { arrArrMenuItemAndMappingToBeCreatedByMenuId, arrErrorIncompleteGroupIdString } =
        await uploadPropertyMenuItemMappingSpreadsheet(event);
      if (arrErrorIncompleteGroupIdString.length > 0) {
        alert(
          `Error/empty cells found, please rectify and try again: ${arrErrorIncompleteGroupIdString.join(
            ', '
          )}`
        );
        return;
      } else if (arrArrMenuItemAndMappingToBeCreatedByMenuId.flat().length === 0) {
        alert('No mappings to be created found');
        return;
      } else {
        // eslint-disable-next-line no-restricted-globals
        const userConfirmation = confirm(
          `Creating mappings for ${
            arrArrMenuItemAndMappingToBeCreatedByMenuId.flat().length
          } internal menu item (groups). Proceed?`
        );
        if (!userConfirmation) {
          return;
        }
      }

      await axios.post('/report-management/create-menu-item-and-mapping', {
        remoteTaggingUserEmail: user?.email,
        arrArrMenuItemAndMappingToBeCreatedByMenuId,
      });

      alert('Mapping creation successful!');
    } catch (error) {
      displayError(ERROR_MESSAGE.fetchPropertyMenuItemMappingUploadError, ERROR_TYPE.fatal);
    } finally {
      // Below removes the value assigned to the input file element so that user may upload the same name file again (with edits)
      // Without the below, re-uploading the same file name will cause the onChange to not be triggered
      event.target.value = '';
      setIsPropertyMenuItemMappingUploadLoading(false);
    }
  };

  /**
   * Remove if the locationId is already in the set and add if the locationId is not yet in the set. This is to update
   * the state setLocationIdExpanded which holds the location(s) where its service list is to be displayed.
   * @param locationId - locationId to be removed or added to setLocationIdExpanded
   */
  const updateSetLocationIdExpanded = (locationId: number) => {
    const updatedSetLocationIdExpanded = new Set(Array.from(setLocationIdExpanded));
    if (updatedSetLocationIdExpanded.has(locationId)) {
      updatedSetLocationIdExpanded.delete(locationId);
    } else {
      updatedSetLocationIdExpanded.add(locationId);
    }
    setSetLocationIdExpanded(updatedSetLocationIdExpanded);
  };

  const renderLocationCheckbox = (arrLocationService: Array<LocationService>) => {
    const { mapSetSelectedServiceIdBySelectedLocationId } = formState;
    return arrLocationService.map((locationService) => {
      const { locationId, name, arrService } = locationService;
      return (
        <div key={`LocationCheckbox ${locationId}`} className="flex flex-col">
          <div
            className="flex items-center shadow-md bg-gray-300 hover:bg-gray-200"
            onClick={() => updateSetLocationIdExpanded(locationId)}
          >
            <Checkbox
              checked={mapSetSelectedServiceIdBySelectedLocationId.has(locationId)}
              id={`LocationCheckbox ${locationId}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                updateLocationServiceFormState(e.target.checked, locationService);
              }}
              icon={<MdCheck className="h-3 w-3" />}
            />
            <p className="text-base font-bold break-words align-middle">{name}</p>
          </div>
          <div
            className={`grid grid-cols-1 gap-2 min-h-[2.8rem] items-center bg-gray-100 rounded-b-md ${
              setLocationIdExpanded.has(locationId) ? 'visible' : 'hidden'
            }`}
          >
            {renderServiceCheckbox(arrService)}
          </div>
        </div>
      );
    });
  };

  const renderServiceCheckbox = (arrService: Array<Service>) => {
    const { mapSetSelectedServiceIdBySelectedLocationId } = formState;
    return arrService.map((service: Service) => (
      <div key={`ServiceCheckbox ${service.serviceId}`} className="flex items-center pl-6">
        <Checkbox
          checked={
            mapSetSelectedServiceIdBySelectedLocationId.has(service.locationId) &&
            mapSetSelectedServiceIdBySelectedLocationId
              .get(service.locationId)!
              .has(service.serviceId)
          }
          id={`ServiceCheckbox ${service.serviceId}`}
          onChange={(e) => {
            updateServiceFormState(e.target.checked, service);
          }}
          icon={<MdCheck className="h-3 w-3" />}
        />
        <p className="text-base font-normal break-words align-middle">{service.name}</p>
      </div>
    ));
  };

  return (
    <div className="p-3 pt-3 flex flex-col">
      {isAlert && (
        <AppAlert
          arrAlertMessage={arrAlertMessage}
          alertHeader={alertHeader}
          handleOpen={() => {
            setIsAlert(!isAlert);
          }}
          isExpanded={isAlert}
        />
      )}
      <EditMonthlyReviewSlideDialog
        isOpened={isEditMonthlyReviewSlideDialogOpened}
        setIsOpened={setIsEditMonthlyReviewSlideDialogOpened}
        restaurantId={currentRestaurantIdEditingMonthlyReviewSlideMetadata}
      />
      <h1 className="font-bold text-[#152445] text-3xl inline-block align-text-bottom">Reports</h1>
      <div className="grow flex flex-wrap self-center justify-end w-full mb-3">
        <SearchBar
          arrCompanyService={arrValidCompanyService}
          updateFunction={updateCompanyLocationFormState}
        />
      </div>
      <div className="grid grid-cols-1 gap-5 w-full border-2 rounded-xl px-5 py-6 mt-1 self-center">
        <div className="flex flex-col xl:flex-row gap-5 xl:gap-0">
          <div className="flex flex-col flex-1 gap-5 px-5">
            <div className="text-xl font-semibold">
              Group:
              <select
                name="groups"
                className="GroupField mt-1 block w-full px-0.5 border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-black cursor-pointer"
                value={formState.companyField}
                onChange={(event) => {
                  updateCompanyFormState(event);
                }}
              >
                <option disabled defaultChecked>
                  Select Group
                </option>
                {arrValidCompanyService.map((companyService: CompanyService, index: number) => (
                  <option
                    key={index}
                    value={`{"companyId": ${companyService.companyId}, "name": "${companyService.name}", "isAirline": ${companyService.isAirline}, "index": ${index}}`}
                  >
                    {companyService.name}
                    {companyService.isAirline && ' ✈️'}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-xl font-semibold flex flex-col">
              Date Range:
              <DatePicker
                range
                rangeHover
                value={formState.dateRange}
                onChange={updateDateRangeFormState}
                weekDays={WEEKDAYS}
                numberOfMonths={2}
                weekStartDayIndex={1}
                minDate={minDate}
                maxDate={new DateObject()}
                format="DD/MM/YYYY"
                showOtherDays
                portal
                render={
                  <InputIcon className="DateRangePicker mt-2 text-base block w-full px-0.5 border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-black cursor-pointer" />
                }
              />
            </div>
            <div className="text-xl font-semibold flex">
              <Button
                color="teal"
                className="YesterdayButton flex-1 px-1 rounded-r-none border-r"
                onClick={() => quickSelectDateRange(QUICK_SELECT_OPTION.yesterday)}
              >
                Yesterday
              </Button>
              <Button
                color="teal"
                className="LastWeekButton flex-1 px-1 rounded-none border-r"
                onClick={() => quickSelectDateRange(QUICK_SELECT_OPTION.lastWeek)}
              >
                Last Week
              </Button>
              <Button
                color="teal"
                className="LastMonthButton flex-1 px-1 rounded-none border-x"
                onClick={() => quickSelectDateRange(QUICK_SELECT_OPTION.lastMonth)}
              >
                Last Month
              </Button>
              <Button
                color="teal"
                className="Last3MonthsButton flex-1 px-1 rounded-none border-x"
                onClick={() => quickSelectDateRange(QUICK_SELECT_OPTION.last3Months)}
              >
                Last 3 Months
              </Button>
              <Button
                color="teal"
                className="FromPropertyEarliestStartDateButton min-w-min text-[10px] flex-1 leading-tight px-1 rounded-l-none border-l py-2"
                disabled={formState.restaurantField === 'Select Property'}
                onClick={() =>
                  quickSelectDateRange(QUICK_SELECT_OPTION.fromPropertyEarliestStartDate)
                }
              >
                Property's earliest baseline start date to end of last month
              </Button>
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-2 px-5 xl:border-l-2">
            <div
              className={`text-xl font-semibold flex-1 flex items-center gap-2 justify-between ${
                formState.companyField === 'Select Group' && 'opacity-30'
              }`}
            >
              <div className="flex-1">
                Property:
                <select
                  name="properties"
                  className="PropertyField mt-1 block w-full px-0.5 border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-black cursor-pointer disabled:opacity-30 disabled:cursor-default"
                  value={formState.restaurantField}
                  disabled={formState.companyField === 'Select Group'}
                  onChange={updateRestaurantFormState}
                >
                  <option disabled defaultChecked>
                    Select Property
                  </option>
                  {formState.companyField !== 'Select Group' &&
                    arrValidCompanyService[
                      Number(JSON.parse(formState.companyField).index)
                    ].arrRestaurantService.map((restaurant: RestaurantService, index: number) => (
                      <option
                        key={index}
                        value={`{"name": "${restaurant.name}", "restaurantId": ${restaurant.restaurantId}, "index": ${index}, "whatsappGroupName": "${restaurant.whatsappGroupName}"}`}
                      >
                        {restaurant.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                {formState.restaurantField !== 'Select Property' && (
                  <Button
                    className="EditMonthlyReviewSlideButton bg-purple-500 p-3 rounded w-32 h-14"
                    onClick={() => {
                      setIsEditMonthlyReviewSlideDialogOpened(
                        !isEditMonthlyReviewSlideDialogOpened
                      );
                      setCurrentRestaurantIdEditingMonthlyReviewSlideMetadata(
                        formState.restaurantField !== 'Select Property'
                          ? JSON.parse(formState.restaurantField).restaurantId
                          : null
                      );
                    }}
                  >
                    Edit Monthly Review Slides
                  </Button>
                )}
              </div>
            </div>

            <div
              className={`text-xl font-semibold flex-1 ${
                formState.restaurantField === 'Select Property' && 'opacity-30'
              }`}
            >
              Location & service:
              <Typography className="text-xs w-inherit">
                *select only 1 location for LOCATION REPORT, LOCATION DAILY WASTE REPORT, BASELINE
                REVIEW and SERVICE IMAGES
              </Typography>
              <div className="grid grid-cols-3 gap-2 min-h-[2.8rem] items-start pt-2 pb-1">
                {formState.restaurantField !== 'Select Property' ? (
                  renderLocationCheckbox(
                    arrValidCompanyService[Number(JSON.parse(formState.companyField).index)]
                      .arrRestaurantService[Number(JSON.parse(formState.restaurantField).index)]
                      .arrLocationService
                  )
                ) : (
                  <div className="text-base">Select Location & Service</div>
                )}
              </div>
            </div>
            <div className="text-xl font-semibold flex-1">
              Data Type:
              <div className="flex mt-2">
                <div className="flex items-center mr-4">
                  <input
                    type="radio"
                    value=""
                    name="data-type-radio-group"
                    checked={formState.isTagged === IS_TAGGED_VALUES.taggedAndResolvable}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                    onChange={() => updateIsTaggedFormState(IS_TAGGED_VALUES.taggedAndResolvable)}
                  />
                  <label
                    htmlFor="inline-2-radio"
                    className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    Tagged And Resolvable
                  </label>
                </div>
                <div className="flex items-center mr-4">
                  <input
                    type="radio"
                    value=""
                    name="data-type-radio-group"
                    checked={formState.isTagged === IS_TAGGED_VALUES.taggedAndMapped}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                    onChange={() => updateIsTaggedFormState(IS_TAGGED_VALUES.taggedAndMapped)}
                  />
                  <label
                    htmlFor="inline-2-radio"
                    className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    Tagged And Mapped
                  </label>
                </div>
                <div className="flex items-center mr-4">
                  <input
                    type="radio"
                    value=""
                    name="data-type-radio-group"
                    checked={formState.isTagged === IS_TAGGED_VALUES.allExceptUntagged}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                    onChange={() => updateIsTaggedFormState(IS_TAGGED_VALUES.allExceptUntagged)}
                  />
                  <label
                    htmlFor="inline-2-radio"
                    className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    All Except Untagged
                  </label>
                </div>
                <div className="flex items-center mr-4">
                  <input
                    type="radio"
                    value=""
                    name="data-type-radio-group"
                    checked={formState.isTagged === IS_TAGGED_VALUES.all}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                    onChange={() => updateIsTaggedFormState(IS_TAGGED_VALUES.all)}
                  />
                  <label
                    htmlFor="inline-radio"
                    className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    All
                  </label>
                </div>
                <div className="flex items-center mr-4">
                  <input
                    type="radio"
                    value=""
                    name="data-type-radio-group"
                    checked={formState.isTagged === IS_TAGGED_VALUES.untagged}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 cursor-pointer"
                    onChange={() => updateIsTaggedFormState(IS_TAGGED_VALUES.untagged)}
                  />
                  <label
                    htmlFor="inline-checked-radio"
                    className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >
                    Untagged
                  </label>
                </div>
              </div>
            </div>
            <div className="text-xl font-semibold flex-1">
              Options:
              <div className="flex p-1 w-fit">
                <Checkbox
                  className="pl-0"
                  checked={formState.includeIssueSlides}
                  id={'IncludeIssueSlidesCheckbox'}
                  onChange={(e) => {
                    updateIncludeIssueSlidesFormState(e.target.checked);
                  }}
                  icon={<MdCheck className="h-2 w-2" />}
                />
                <div>
                  <Typography className="text-base font-semibold">Include Issue Slides</Typography>
                  <Typography className="text-xs">
                    *only applicable to Property Monthly Review and Property Top Waste
                  </Typography>
                </div>
              </div>
              <div className="flex p-1 w-fit">
                <Checkbox
                  className="pl-0"
                  checked={formState.includeMenuItemName}
                  id={'IncludeMenuItemNameCheckbox'}
                  onChange={(e) => {
                    updateIncludeMenuItemNameFormState(e.target.checked);
                  }}
                  icon={<MdCheck className="h-2 w-2" />}
                />
                <div>
                  <Typography className="text-base font-semibold">
                    Include Menu Item Name
                  </Typography>
                  <Typography className="text-xs">
                    *only applicable to Property Top Waste
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          {!error.isVisible && (
            <div className="flex gap-2 flex-col xl:flex-row">
              <div className="flex-1 flex">
                <Button
                  className={`GroupReportButton bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-r-none ${
                    isCompanyReportLoading && 'pointer-events-none focus:bg-opacity-60'
                  }`}
                  disabled={
                    formState.companyField === 'Select Group' || formState.dateRange.length !== 2
                  }
                  onClick={generateGroupReport}
                >
                  {isCompanyReportLoading ? (
                    <LoadingSpinnerWithCross
                      cancelDownloadOnClick={() =>
                        cancelDownloadOnClick(
                          groupReportController,
                          isGroupReportCancelledRef,
                          setIsCompanyReportLoading
                        )
                      }
                    />
                  ) : (
                    'Group Report (.csv)'
                  )}
                </Button>
                <Button
                  className={`PropertyReportButton bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-none ${
                    isRestaurantReportLoading && 'pointer-events-none focus:bg-opacity-60'
                  }`}
                  disabled={
                    formState.companyField === 'Select Group' ||
                    formState.restaurantField === 'Select Property' ||
                    formState.dateRange.length !== 2
                  }
                  onClick={() => generateReport('restaurant')}
                >
                  {isRestaurantReportLoading ? (
                    <LoadingSpinnerWithCross
                      cancelDownloadOnClick={() =>
                        cancelDownloadOnClick(
                          propertyReportController,
                          isPropertyReportCancelledRef,
                          setIsRestaurantReportLoading
                        )
                      }
                    />
                  ) : (
                    'Property Report (.csv)'
                  )}
                </Button>
                <Button
                  className={`LocationReportButton bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-l-none ${
                    isLocationReportLoading && 'pointer-events-none focus:bg-opacity-60'
                  }`}
                  disabled={
                    formState.companyField === 'Select Group' ||
                    formState.restaurantField === 'Select Property' ||
                    formState.mapSetSelectedServiceIdBySelectedLocationId.size !== 1 ||
                    formState.dateRange.length !== 2
                  }
                  onClick={() => generateReport('location')}
                >
                  {isLocationReportLoading ? (
                    <LoadingSpinnerWithCross
                      cancelDownloadOnClick={() =>
                        cancelDownloadOnClick(
                          locationReportController,
                          isLocationReportCancelledRef,
                          setIsLocationReportLoading
                        )
                      }
                    />
                  ) : (
                    'Location Report (.csv)'
                  )}
                </Button>
              </div>
              <div className="flex-1 flex">
                <Button
                  className={`bg-regal-blue p-3 focus:opacity-100 flex-1 rounded-r-none ${
                    isCompanyDailyWasteReportLoading && 'pointer-events-none focus:bg-opacity-60'
                  }`}
                  disabled={
                    formState.companyField === 'Select Group' || formState.dateRange.length !== 2
                  }
                  onClick={() => generateDailyWasteReport('company')}
                >
                  {isCompanyDailyWasteReportLoading ? (
                    <LoadingSpinnerWithCross
                      cancelDownloadOnClick={() =>
                        cancelDownloadOnClick(
                          groupDailyWasteReportController,
                          isGroupDailyWasteReportCancelledRef,
                          setIsCompanyDailyWasteReportLoading
                        )
                      }
                    />
                  ) : (
                    'Group Daily Waste Report (.csv)'
                  )}
                </Button>
                <Button
                  className={`bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-none ${
                    isRestaurantDailyWasteReportLoading && 'pointer-events-none focus:bg-opacity-60'
                  }`}
                  disabled={
                    formState.companyField === 'Select Group' ||
                    formState.restaurantField === 'Select Property' ||
                    formState.dateRange.length !== 2
                  }
                  onClick={() => generateDailyWasteReport('restaurant')}
                >
                  {isRestaurantDailyWasteReportLoading ? (
                    <LoadingSpinnerWithCross
                      cancelDownloadOnClick={() =>
                        cancelDownloadOnClick(
                          propertyDailyWasteReportController,
                          isPropertyDailyWasteReportCancelledRef,
                          setIsRestaurantDailyWasteReportLoading
                        )
                      }
                    />
                  ) : (
                    'Property Daily Waste Report (.csv)'
                  )}
                </Button>
                <Button
                  className={`bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-l-none ${
                    isLocationDailyWasteReportLoading && 'pointer-events-none focus:bg-opacity-60'
                  }`}
                  disabled={
                    formState.companyField === 'Select Group' ||
                    formState.restaurantField === 'Select Property' ||
                    formState.mapSetSelectedServiceIdBySelectedLocationId.size !== 1 ||
                    formState.dateRange.length !== 2
                  }
                  onClick={() => generateDailyWasteReport('location')}
                >
                  {isLocationDailyWasteReportLoading ? (
                    <LoadingSpinnerWithCross
                      cancelDownloadOnClick={() =>
                        cancelDownloadOnClick(
                          locationDailyWasteReportController,
                          isLocationDailyWasteReportCancelledRef,
                          setIsLocationDailyWasteReportLoading
                        )
                      }
                    />
                  ) : (
                    'Location Daily Waste Report (.csv)'
                  )}
                </Button>
              </div>
              <div className="flex flex-1 h-full flex-col items-center gap-1">
                <div className="flex h-full w-full">
                  <Button
                    className={`SingleDayPropertyTopWasteButton bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-r-none ${
                      isSingleDayPropertyTopWasteLoading &&
                      'pointer-events-none focus:bg-opacity-60'
                    }`}
                    disabled={
                      formState.companyField === 'Select Group' ||
                      formState.restaurantField === 'Select Property' ||
                      formState.mapSetSelectedServiceIdBySelectedLocationId.size === 0 ||
                      formState.dateRange.length !== 2
                    }
                    onClick={() => downloadPropertyTopWaste(false)}
                  >
                    {isSingleDayPropertyTopWasteLoading ? (
                      <LoadingSpinnerWithCross
                        cancelDownloadOnClick={() =>
                          cancelDownloadOnClick(
                            singleDayPropertyTopWasteController,
                            isSingleDayPropertyTopWasteCancelledRef,
                            setIsSingleDayPropertyTopWasteLoading
                          )
                        }
                      />
                    ) : (
                      'Single-Day Property Top Waste (.jpeg)'
                    )}
                  </Button>
                  <Button
                    className={`MultiDayPropertyTopWasteButton bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-l-none ${
                      isMultiDayPropertyTopWasteLoading && 'pointer-events-none focus:bg-opacity-60'
                    }`}
                    disabled={
                      formState.companyField === 'Select Group' ||
                      formState.restaurantField === 'Select Property' ||
                      formState.mapSetSelectedServiceIdBySelectedLocationId.size === 0 ||
                      formState.dateRange.length !== 2
                    }
                    onClick={() => downloadPropertyTopWaste(true)}
                  >
                    {isMultiDayPropertyTopWasteLoading ? (
                      <LoadingSpinnerWithCross
                        cancelDownloadOnClick={() =>
                          cancelDownloadOnClick(
                            multiDayPropertyTopWasteController,
                            isMultiDayPropertyTopWasteCancelledRef,
                            setIsMultiDayPropertyTopWasteLoading
                          )
                        }
                      />
                    ) : (
                      'Multi-Day Property Top Waste (.jpeg)'
                    )}
                  </Button>
                </div>
                {isWhatsappLoading ? (
                  <LoadingSpinner />
                ) : (
                  formState.restaurantField !== 'Select Property' &&
                  JSON.parse(formState.restaurantField).whatsappGroupName !== 'null' &&
                  arrWhatsappMessagingSessionOption !== null &&
                  formState.mapSetSelectedServiceIdBySelectedLocationId.size !== 0 && (
                    <>
                      {whatsappMessagingSessionSelected !== 'Select WA Session' && (
                        <div className="flex h-full w-full">
                          <Button
                            className="SingleDayPropertyTopWasteWhatsappButton bg-green-400 flex justify-center items-center flex-1 p-1 rounded-r-none gap-1"
                            onClick={() => downloadPropertyTopWaste(false, true)}
                          >
                            {isSingleDayPropertyTopWasteLoading ? (
                              <LoadingSpinner />
                            ) : (
                              <>
                                <MdWhatsapp size="1.5em" />
                                <span className="text-[0.5rem]">
                                  {JSON.parse(formState.restaurantField).whatsappGroupName}
                                </span>
                              </>
                            )}
                          </Button>
                          <Button
                            className="MultiDayPropertyTopWasteWhatsappButton bg-green-400 flex justify-center items-center p-1 flex-1 rounded-l-none gap-1"
                            onClick={() => downloadPropertyTopWaste(true, true)}
                          >
                            {isMultiDayPropertyTopWasteLoading ? (
                              <LoadingSpinner />
                            ) : (
                              <>
                                <MdWhatsapp size="1.5em" />
                                <span className="text-[0.5rem]">
                                  {JSON.parse(formState.restaurantField).whatsappGroupName}
                                </span>
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      <select
                        value={whatsappMessagingSessionSelected}
                        onChange={(event) =>
                          setWhatsappMessagingSessionSelected(event.target.value)
                        }
                        className="mt-1 block w-full p-0.5 border-0 border-b-2 border-gray-200 focus:ring-0 focus:border-black cursor-pointer disabled:opacity-30 disabled:cursor-default"
                      >
                        <option disabled defaultChecked>
                          Select WA Session
                        </option>
                        {arrWhatsappMessagingSessionOption.map(
                          (whatsappMessagingSessionOption, index) => (
                            <option
                              key={`whatsapp_session_${index}`}
                              value={whatsappMessagingSessionOption}
                            >
                              {whatsappMessagingSessionOption}
                            </option>
                          )
                        )}
                      </select>
                    </>
                  )
                )}
              </div>

              <div className="flex flex-col flex-1">
                <div className="rounded-lg p-2 rounded-b-none uppercase text-xs bg-blue-gray-900 text-center text-white font-semibold">
                  Property Menu Item Mapping
                </div>
                <div className="flex flex-1">
                  <Button
                    className={`PropertyMenuItemMappingButton bg-regal-blue focus:opacity-100 flex-1 p-0 rounded-t-none rounded-r-none ${
                      isPropertyMenuItemMappingDownloadLoading &&
                      'pointer-events-none focus:bg-opacity-60'
                    }`}
                    disabled={
                      formState.companyField === 'Select Group' ||
                      formState.restaurantField === 'Select Property' ||
                      formState.mapSetSelectedServiceIdBySelectedLocationId.size === 0
                    }
                    onClick={downloadPropertyMenuItemMapping}
                  >
                    {isPropertyMenuItemMappingDownloadLoading ? (
                      <LoadingSpinnerWithCross
                        cancelDownloadOnClick={() =>
                          cancelDownloadOnClick(
                            propertyMenuItemMappingDownloadController,
                            isPropertyMenuItemMappingDownloadCancelledRef,
                            setIsPropertyMenuItemMappingDownloadLoading
                          )
                        }
                      />
                    ) : (
                      'Download (.xlsx)'
                    )}
                  </Button>
                  <Button className="PropertyMenuItemMappingUploadButton bg-regal-blue flex-1 rounded-l-none rounded-t-none p-0">
                    <label
                      htmlFor="inputFormId"
                      // 'any' type is given as the event is provided by the input element (via htmlFor), not the label element
                      onChange={(event) => uploadPropertyMenuItemMapping(event as any)}
                      className="flex p-3 justify-center !cursor-pointer"
                    >
                      <input
                        name=""
                        type="file"
                        id="inputFormId"
                        disabled={isPropertyMenuItemMappingUploadLoading}
                        hidden
                      />
                      {isPropertyMenuItemMappingUploadLoading ? (
                        <LoadingSpinner />
                      ) : (
                        'Upload (.xlsx)'
                      )}
                    </label>
                  </Button>
                </div>
              </div>

              <div className="flex-1 flex">
                <Button
                  className={`BaselineReviewButton bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-r-none ${
                    isBaselineReviewLoading && 'pointer-events-none focus:bg-opacity-60'
                  }`}
                  disabled={
                    formState.companyField === 'Select Group' ||
                    formState.restaurantField === 'Select Property' ||
                    formState.mapSetSelectedServiceIdBySelectedLocationId.size !== 1 ||
                    formState.dateRange.length !== 2 ||
                    formState.isTagged === IS_TAGGED_VALUES.all ||
                    formState.isTagged === IS_TAGGED_VALUES.untagged
                  }
                  onClick={downloadBaselineReview}
                >
                  {isBaselineReviewLoading ? (
                    <LoadingSpinnerWithCross
                      cancelDownloadOnClick={() =>
                        cancelDownloadOnClick(
                          baselineReviewController,
                          isBaselineReviewCancelledRef,
                          setIsBaselineReviewLoading
                        )
                      }
                    />
                  ) : (
                    'Baseline Review (.pptx)'
                  )}
                </Button>
                <Button
                  className={`PropertyMonthlyReviewButton bg-regal-blue focus:opacity-100 p-3 flex-1 rounded-l-none ${
                    isPropertyMonthlyReviewLoading && 'pointer-events-none focus:bg-opacity-60'
                  }`}
                  disabled={
                    formState.companyField === 'Select Group' ||
                    formState.restaurantField === 'Select Property' ||
                    formState.mapSetSelectedServiceIdBySelectedLocationId.size === 0 ||
                    formState.dateRange.length !== 2 ||
                    formState.isTagged === IS_TAGGED_VALUES.all ||
                    formState.isTagged === IS_TAGGED_VALUES.untagged
                  }
                  onClick={downloadPropertyMonthlyReview}
                >
                  {isPropertyMonthlyReviewLoading ? (
                    <LoadingSpinnerWithCross
                      cancelDownloadOnClick={() =>
                        cancelDownloadOnClick(
                          propertyMonthlyReviewController,
                          isPropertyMonthlyReviewCancelledRef,
                          setIsPropertyMonthlyReviewLoading
                        )
                      }
                    />
                  ) : (
                    'Property Monthly Review (.pptx)'
                  )}
                </Button>
              </div>
              <Button
                className={`ServiceImageButton bg-regal-blue focus:opacity-100 p-3 flex-1 ${
                  isImageLoading && 'pointer-events-none focus:bg-opacity-60'
                }`}
                disabled={
                  formState.companyField === 'Select Group' ||
                  formState.restaurantField === 'Select Property' ||
                  formState.mapSetSelectedServiceIdBySelectedLocationId.size !== 1 ||
                  formState.dateRange.length !== 2
                }
                onClick={downloadImage}
              >
                {isImageLoading ? (
                  <LoadingSpinnerWithCross
                    cancelDownloadOnClick={() =>
                      cancelDownloadOnClick(
                        serviceImagesController,
                        isServiceImagesCancelledRef,
                        setIsImageLoading
                      )
                    }
                  />
                ) : (
                  'Service Images (.zip)'
                )}
              </Button>
            </div>
          )}
          {error.isVisible && (
            <Alert
              className={error.errorType === ERROR_TYPE.fatal ? 'bg-red-500' : 'bg-purple-500'}
              variant="filled"
              dismissible={{
                onClose: resetErrorMessage,
              }}
            >
              {error.message}
            </Alert>
          )}
          <Button color="amber" className="mt-3" onClick={resetForm}>
            Reset Fields
          </Button>
        </div>
      </div>
      {/* Below div is a hidden element required for the preparation of property top wastes */}
      <div className="relative overflow-hidden">
        <div ref={propertyTopWasteRef} className="flex flex-col gap-2 p-3 absolute overflow-hidden">
          <>
            <div className="flex mb-4 justify-between">
              <div className="flex items-end gap-2">
                <span className="font-bold text-4xl">{dataToDisplay?.locationName}</span>
                <span className="text-xl italic">({dataToDisplay?.date})</span>
              </div>
              <img src={LumiticsLogo} alt="" />
            </div>
            {dataToDisplay?.arrDateTopIncorrectSmartStartSignedUrl.length > 0 && (
              <Card
                className="border-4 border-red-500 min-w-[1500px] bg-white"
                key={`location_${dataToDisplay?.date}_${dataToDisplay?.locationName}`}
              >
                <CardBody className="grid grid-cols-10 gap-4">
                  <div className="flex flex-col justify-center items-center col-span-1">
                    <span className="font-bold text-xl text-center">Wrong Start State</span>
                  </div>
                  <div className="flex flex-col gap-2 col-span-9">
                    <div className="grid gap-2 grid-cols-6 min-h-[9rem]">
                      {dataToDisplay?.arrDateTopIncorrectSmartStartSignedUrl.map(
                        (
                          { date, signedUrl }: DateTopWasteImageSignedUrlByWeight,
                          index: number
                        ) => {
                          return (
                            <div
                              key={`location_${dataToDisplay?.date}_${dataToDisplay?.locationName}_link_${index}`}
                              className="relative"
                            >
                              <img src={signedUrl} alt="" />
                              {date && (
                                <div className="absolute bottom-0 w-full text-center bg-black/60">
                                  <span className="text-white/90">{date}</span>
                                </div>
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
            {dataToDisplay?.arrDateTopWasteDetailByService.map(
              (dateTopWasteDetailByService: DateTopWasteDetailByService) => {
                const {
                  serviceName,
                  totalNumberOfWaste,
                  totalWeight,
                  arrDateTopWasteImageSignedUrlByWeight,
                  arrDateTopMixedWasteImageSignedUrlByWeight,
                  arrDateTopDuplicateWasteImageSignedUrlByWeight,
                  arrDateTopUsageWasteImageSignedUrlByWeight,
                } = dateTopWasteDetailByService;
                return (
                  <Card
                    className="border-2 min-w-[1500px] bg-white"
                    key={`service_${dataToDisplay?.date}_${serviceName}`}
                  >
                    <CardBody className="grid grid-cols-10 gap-4">
                      <div className="flex flex-col justify-center items-center col-span-1 relative">
                        <span className="font-bold text-xl text-center">{serviceName}</span>
                        {totalNumberOfWaste !== 0 && (
                          <>
                            <span>{totalNumberOfWaste} throws</span>
                            <span>{totalWeight.toFixed(2)} KG</span>
                          </>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 col-span-9">
                        <div className="grid gap-2 grid-cols-6 p-1 border-4 border-transparent rounded-md">
                          {arrDateTopWasteImageSignedUrlByWeight.map(
                            (
                              { date, name, signedUrl }: DateTopWasteImageSignedUrlByWeight,
                              index: number
                            ) => {
                              return (
                                <div
                                  key={`service_${dataToDisplay?.date}_${serviceName}_link_${index}`}
                                  className="relative"
                                >
                                  <img src={signedUrl} alt="" />
                                  <div className="absolute bottom-0 w-full">
                                    {formState.includeMenuItemName && name && (
                                      <div className="text-center bg-black/60">
                                        <span className="text-white/90">{name}</span>
                                      </div>
                                    )}
                                    {date && (
                                      <div className="text-center bg-black/60">
                                        <span className="text-white/90">{date}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                        {arrDateTopMixedWasteImageSignedUrlByWeight.length > 0 && (
                          <div className="flex flex-col p-1 border-4 border-red-500 rounded-md">
                            <span className="italic">Mixed</span>
                            <div className="grid gap-2 grid-cols-6">
                              {arrDateTopMixedWasteImageSignedUrlByWeight.map(
                                (
                                  { date, signedUrl }: DateTopWasteImageSignedUrlByWeight,
                                  index: number
                                ) => {
                                  return (
                                    <div
                                      key={`service_${dataToDisplay?.date}_${serviceName}_link_${index}`}
                                      className="relative"
                                    >
                                      <img src={signedUrl} alt="" />
                                      {date && (
                                        <div className="absolute bottom-0 w-full text-center bg-black/60">
                                          <span className="text-white/90">{date}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        )}
                        {arrDateTopDuplicateWasteImageSignedUrlByWeight.length > 0 && (
                          <div className="flex flex-col p-1 border-4 border-red-500 rounded-md">
                            <span className="italic">Duplicate</span>
                            <div className="grid gap-2 grid-cols-6">
                              {arrDateTopDuplicateWasteImageSignedUrlByWeight.map(
                                (
                                  { date, signedUrl }: DateTopWasteImageSignedUrlByWeight,
                                  index: number
                                ) => {
                                  return (
                                    <div
                                      key={`service_${dataToDisplay?.date}_${serviceName}_link_${index}`}
                                      className="relative"
                                    >
                                      <img src={signedUrl} alt="" />
                                      {date && (
                                        <div className="absolute bottom-0 w-full text-center bg-black/60">
                                          <span className="text-white/90">{date}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        )}
                        {arrDateTopUsageWasteImageSignedUrlByWeight.length > 0 && (
                          <div className="flex flex-col p-1 border-4 border-red-500 rounded-md">
                            <span className="italic">Usage Issue</span>
                            <div className="grid gap-2 grid-cols-6">
                              {arrDateTopUsageWasteImageSignedUrlByWeight.map(
                                (
                                  { date, signedUrl }: DateTopWasteImageSignedUrlByWeight,
                                  index: number
                                ) => {
                                  return (
                                    <div
                                      key={`service_${dataToDisplay?.date}_${serviceName}_link_${index}`}
                                      className="relative"
                                    >
                                      <img src={signedUrl} alt="" />
                                      {date && (
                                        <div className="absolute bottom-0 w-full text-center bg-black/60">
                                          <span className="text-white/90">{date}</span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                );
              }
            )}
          </>
        </div>
      </div>
    </div>
  );
};

export default ReportInputCard;
